/* ============================================================
   Number Sense — Nashville Number System trainer data
   ------------------------------------------------------------
   A gamified companion to the /keyboard lesson plan. The musical
   facts (diatonic chord tables, starter songs, audio) all live in
   keyboardPlan.js — this file only adds the curriculum (UNITS),
   the dynamic lesson builder, and the small helpers the page and
   practice games need. Nothing here re-defines a chord.

   A "concept" here is a (key, degree) pair, e.g. "C:IV" — the
   analog of a vocabulary word. A concept is unlocked once the
   lesson that introduces it is marked complete.
   ============================================================ */

import {
  DIATONIC,
  DIATONIC7,
  DIATONIC_MINOR,
  STARTER_SONGS,
  SEMITONES,
  scaleFor,
  shellVoicing,
  groupStrikes,
  scoreRhythm,
  REST,
} from "./keyboardPlan";

// A rest bar in a loop — the groove owns the token (the drummer fills it),
// the curriculum re-exports it so questions and screens agree on the marker.
export { REST };

// The four major keys keyboardPlan defines a full diatonic table for.
export const KEYS = ["C", "G", "D", "A"];

// The four minor keys (relative minors of the majors above).
export const MINOR_KEYS = Object.keys(DIATONIC_MINOR);

// Every key, major then minor — for stable cross-track sorting in stats.
const ALL_KEYS = [...KEYS, ...MINOR_KEYS];

// Degree order, for distractor pools.
export const DEGREES = ["I", "ii", "iii", "IV", "V", "vi", "vii°"];

// The seventh-chord degrees (Imaj7 … vii°7), the major track's advanced tier.
// Sourced from DIATONIC7 so there's a single source of truth for the tokens.
export const SEVENTH_DEGREES = (DIATONIC7.C || []).map((c) => c.deg);

// The natural-minor degrees (i, ii°, III, iv, v, VI, VII), the minor track.
export const MINOR_DEGREES = (DIATONIC_MINOR.Am || []).map((c) => c.deg);

// Combined degree order for stable sorting once sevenths / minor enter the mix.
const ALL_DEGREES = [...DEGREES, ...MINOR_DEGREES, ...SEVENTH_DEGREES];

// Which reference table a (key, degree) belongs to. A minor key name (Am, Em…)
// is unambiguous, so it wins; otherwise a seventh degree picks the 7th table.
const isSeventh = (deg) => SEVENTH_DEGREES.includes(deg);
const isMinorDeg = (deg) => MINOR_DEGREES.includes(deg);
const tableFor = (key, deg) => {
  if (DIATONIC_MINOR[key]) return DIATONIC_MINOR[key];
  if (isSeventh(deg)) return DIATONIC7[key] || [];
  return DIATONIC[key] || [];
};

// A handful of real, common four-chord loops — used as plausible distractors in
// ear / progression / song questions, and as the "mixed" unit's material.
const COMMON_PROGS = [
  ["I", "V", "vi", "IV"],
  ["I", "vi", "IV", "V"],
  ["vi", "IV", "I", "V"],
  ["I", "IV", "V", "I"],
  ["I", "IV", "vi", "V"],
  ["I", "V", "ii", "IV"],
  ["ii", "V", "I", "I"],
  ["I", "vi", "ii", "V"],
];

// Signature natural-minor loops — the minor track's progression material and
// the distractor pool for minor ear/progression questions.
const MINOR_PROGS = [
  ["i", "VI", "III", "VII"],
  ["i", "VII", "VI", "VII"],
  ["i", "iv", "v", "i"],
  ["i", "VI", "VII", "i"],
  ["i", "iv", "VII", "III"],
  ["i", "v", "VI", "iv"],
];

// The right progression pool for a key (minor keys draw minor loops).
const progPoolFor = (key) => (DIATONIC_MINOR[key] ? MINOR_PROGS : COMMON_PROGS);

// Every key the jam room offers (major then minor) and the loops to comp over in
// a given key. The jam room is an open practice space, so it isn't unlock-gated.
export const JAM_KEYS = [...KEYS, ...MINOR_KEYS];
export const jamProgsFor = (key) => (DIATONIC_MINOR[key] ? MINOR_PROGS : COMMON_PROGS);

/* ---------------- the curriculum ----------------
   The major track is one unit per key, each with the same four-lesson arc plus
   a "put it together" capstone (the capstone node is added by trackPath, not
   listed here). A unit is described by a `plan` — an ordered list of lessons:

     { kind: "scale" }                              the key's scale, numbered
     { kind: "chords", quality, degrees }           the major / minor diatonic chords
     { kind: "prog", progs: [[...], ...] }          progressions (prior + a new one)

   Plan units carry: { id, title, gloss, key, plan, prog (signature loop for the
   capstone), song? }. The lesson builder dispatches on each plan lesson's kind
   (see buildPlanLesson); unlockedConcepts reads the `chords` lessons' degrees.

   `degrees`/`lessons`/`modes`/`seventh` (the older shape) still describe the
   bonus "add the 7th" unit and the voicing track. */

// Diatonic chords of a major key, split into the lessons the user asked for:
// the bright I·IV·V, then the minor ii·iii·vi (vii° is left out of the drills).
const MAJOR_CHORD_LESSONS = [
  { kind: "chords", quality: "major", degrees: ["I", "IV", "V"] },
  { kind: "chords", quality: "minor", degrees: ["ii", "iii", "vi"] },
];

// The common major progressions — exactly one introduced per key (foundational
// first, then richer). Each key's prog lesson gets every loop introduced so far
// (the earlier keys' loops carry forward and get reinforced) plus the one that
// key adds, so the vocabulary builds up as you move through the keys.
const MAJOR_PROG_SEQUENCE = [
  ["I", "IV", "V", "I"], // the workhorse — introduced with C
  ["I", "V", "vi", "IV"], // four-chord pop — added in G
  ["I", "vi", "IV", "V"], // the 50s / doo-wop turn — added in D
  ["vi", "IV", "I", "V"], // the axis loop — added in A
];
// Loops known through the k-th key (one per key: C=0 → [first], then +1 each).
const majorProgsThrough = (k) => MAJOR_PROG_SEQUENCE.slice(0, k + 1);

// One major-key unit: scale → major chords → minor chords → progressions. The
// capstone's signature loop is the newest one this key introduced.
const majorKeyUnit = (id, key, title, gloss, progs, song) => ({
  id,
  title,
  gloss,
  key,
  plan: [
    { kind: "scale" },
    ...MAJOR_CHORD_LESSONS,
    { kind: "prog", progs },
  ],
  prog: progs[progs.length - 1],
  ...(song ? { song } : {}),
});

export const UNITS = [
  majorKeyUnit(
    "kc", "C", "the key of C", "scale · chords · progressions",
    majorProgsThrough(0), "Let It Be"
  ),
  majorKeyUnit(
    "kg", "G", "the key of G", "same numbers, new home",
    majorProgsThrough(1), "Knockin' on Heaven's Door"
  ),
  majorKeyUnit(
    "kd", "D", "the key of D", "portability proven",
    majorProgsThrough(2), "With or Without You"
  ),
  majorKeyUnit(
    "ka", "A", "the key of A", "the whole system, anywhere",
    majorProgsThrough(3), "Stand By Me"
  ),
  // The advanced unit: seventh chords in C — a full plan unit (its own chord
  // lessons, a 7th-progressions lesson, and a capstone), but flagged `seventh`
  // so its 4-note chords stay out of the triad-based practice games.
  {
    id: "u8",
    title: "add the 7th",
    gloss: "Imaj7 · V7 · ii7 …",
    key: "C",
    seventh: true,
    plan: [
      {
        kind: "chords",
        seventh: true,
        tag: "7ths",
        name: "the major & dominant 7ths",
        degrees: ["Imaj7", "IVmaj7", "V7"],
      },
      {
        kind: "chords",
        seventh: true,
        tag: "more 7ths",
        name: "the minor 7ths",
        degrees: ["ii7", "iii7", "vi7"],
      },
      {
        kind: "prog",
        // the ii–V–I is the focus (added last); the I–vi–ii–V turnaround backs it
        progs: [["Imaj7", "vi7", "ii7", "V7"], ["ii7", "V7", "Imaj7"]],
      },
    ],
    prog: ["ii7", "V7", "Imaj7"],
  },
];

// Lessons in a unit — a plan unit's lesson count is its plan length.
export const lessonCount = (u) => (u.plan ? u.plan.length : u.lessons || 0);

export const TOTAL_LESSONS = UNITS.reduce((n, u) => n + lessonCount(u), 0);

/* ---------------- the minor track ----------------
   The same one-unit-per-key shape as the major track, for natural-minor keys:
   scale → the minor home chords (i·iv·v) → the major "brighteners" (III·VI·VII)
   → progressions. Every unit carries `minor: true` so the builder draws minor
   keys / loops, and the capstone skips the major-only function-ear check. Unit
   ids are globally unique so completion never collides with the major track. */

// A minor key's diatonic chords, split into two lessons: the dark home triads,
// then the major chords that brighten a minor song.
const MINOR_CHORD_LESSONS = [
  { kind: "chords", quality: "minor", degrees: ["i", "iv", "v"] },
  { kind: "chords", quality: "major", degrees: ["III", "VI", "VII"] },
];

// The common natural-minor progressions — one per key, same build-up idea.
const MINOR_PROG_SEQUENCE = [
  ["i", "iv", "v", "i"], // the minor workhorse — Am
  ["i", "VI", "III", "VII"], // the minor anthem / sad-pop loop — Em
  ["i", "VII", "VI", "VII"], // a descending vamp — Bm
];
const minorProgsThrough = (k) => MINOR_PROG_SEQUENCE.slice(0, k + 1);

const minorKeyUnit = (id, key, title, gloss, progs) => ({
  id,
  title,
  gloss,
  key,
  minor: true,
  plan: [
    { kind: "scale" },
    ...MINOR_CHORD_LESSONS,
    { kind: "prog", progs },
  ],
  prog: progs[progs.length - 1],
});

export const MINOR_UNITS = [
  minorKeyUnit(
    "kam", "Am", "the key of A minor", "scale · chords · progressions",
    minorProgsThrough(0)
  ),
  minorKeyUnit(
    "kem", "Em", "the key of E minor", "same numbers, new home",
    minorProgsThrough(1)
  ),
  minorKeyUnit(
    "kbm", "Bm", "the key of B minor", "portability, minor",
    minorProgsThrough(2)
  ),
];

/* ---------------- the comping & voicing track ----------------
   A third curriculum that teaches *which notes to actually play* in a band:
   guide tones (shell / rootless voicings), leaving the root for the bassist so
   the section doesn't get muddy. Every unit carries `voicing: true` so the
   lesson builder emits voicing questions (graded as a guide-tone-only set —
   playing the root fails, which is the whole point). `seventhVoicing` units roam
   every key the way the seventh unit does, since 7ths are taught everywhere.
   Unit ids are globally unique (vc1…) so completion never collides. Voicing
   units are deliberately kept out of the triad practice games and the unlock
   path (see unlockedConcepts / gameUnlockPoint). */
export const VOICING_UNITS = [
  {
    id: "vc1",
    title: "leave room for the bass",
    gloss: "rootless triads · 3rd & 5th",
    key: "C",
    voicing: true,
    degrees: ["I", "IV", "V", "vi"], // for the capstone
    prog: ["I", "V", "vi", "IV"],
    plan: [
      { kind: "voicing", degrees: ["I", "IV", "V", "vi"] },
      { kind: "comp", prog: ["I", "V", "vi", "IV"] },
    ],
  },
  {
    id: "vc2",
    title: "shell sevenths",
    gloss: "just the 3rd & 7th",
    key: "C",
    voicing: true,
    degrees: ["Imaj7", "ii7", "IVmaj7", "V7", "vi7"],
    prog: ["ii7", "V7", "Imaj7"],
    plan: [
      {
        kind: "voicing",
        seventh: true,
        degrees: ["Imaj7", "ii7", "IVmaj7", "V7", "vi7"],
      },
      { kind: "comp", seventh: true, prog: ["ii7", "V7", "Imaj7"] },
      // the 3rd↔7th handoff: voice-lead the shells through the ii–V–I
      { kind: "voicelead", shell: true, prog: ["ii7", "V7", "Imaj7"] },
    ],
  },
  {
    id: "vc3",
    title: "smooth changes",
    gloss: "inversions · voice leading",
    key: "C",
    voicing: true,
    inversions: true,
    degrees: ["I", "IV", "V", "vi"],
    prog: ["I", "vi", "IV", "V"],
    plan: [
      { kind: "inversion", degrees: ["I", "IV", "V", "vi"] },
      { kind: "voicelead", prog: ["I", "vi", "IV", "V"] },
    ],
  },
  {
    id: "vc4",
    title: "color & motion",
    gloss: "sus & add9 · bass walk-ups",
    key: "C",
    voicing: true,
    color: true,
    degrees: ["I", "IV", "V"],
    prog: ["I", "V", "vi", "IV"],
    plan: [
      { kind: "color" }, // sus2 / sus4 / add9: build them, then resolve them
      { kind: "walk" }, // slash chords as bass motion: walk up, walk down
    ],
  },
  {
    id: "vc5",
    title: "leave room",
    gloss: "rests · when not to play",
    key: "C",
    voicing: true,
    space: true,
    degrees: ["I", "V", "vi"],
    prog: ["I", "V", "vi", "IV"],
    plan: [
      { kind: "space" }, // comp three bars, lay out for the fill
    ],
  },
];

/* The selectable tracks shown on the home screen. Each has its own unit path;
   XP / streak / mastery / history stay shared across tracks. */
export const TRACKS = [
  {
    id: "major",
    title: "major keys",
    gloss: "I · IV · V and the four-chord world",
    units: UNITS,
  },
  {
    id: "minor",
    title: "minor keys",
    gloss: "i · iv · v and the minor loops",
    units: MINOR_UNITS,
  },
  {
    id: "voicing",
    title: "comping & voicing",
    gloss: "play the right notes, leave room for the band",
    units: VOICING_UNITS,
  },
];

// Total lessons in a track's unit path (for the home progress footnote).
export const trackLessons = (units = []) => units.reduce((n, u) => n + lessonCount(u), 0);

/* ---------------- teaching content ----------------
   Short "learn" slides (concept → example → let's-try) shown before a unit's
   first lesson and browsable any time in the Learn library. Kept as a map keyed
   by unit id so the curriculum objects stay lean. A slide may carry an optional
   `example: { key, degrees }` — the Learn screen renders it as tappable chord
   chips with a "hear it" button, so the slides aren't text-only. */
const TEACH = {
  u1: [
    {
      title: "think in numbers",
      body: "Every major key has 7 chords. Number them by the scale: in C, C=1, D=2, E=3, F=4, G=5, A=6, B=7. We write them as roman numerals — I, IV, V.",
      example: { key: "C", degrees: ["I", "ii", "iii", "IV", "V", "vi", "vii°"] },
    },
    {
      title: "the big three",
      body: "I, IV and V are the workhorses — major chords on the 1st, 4th and 5th notes. In C: I=C, IV=F, V=G. Thousands of songs live on just these three.",
      example: { key: "C", degrees: ["I", "IV", "V"] },
    },
    {
      title: "how a chord is built",
      body: "A major chord (a triad) is three notes stacked by a fixed pattern: start on the root, go up 4 half-steps, then up 3 more. C → C, E, G. It's one rule from any starting note — that's all I, IV and V really are.",
      recipe: { key: "C", deg: "I" },
    },
    {
      title: "why bother?",
      body: "Numbers are portable. \"I–IV–V\" is the same move in every key — learn it once, play it anywhere. Let's start in C.",
      example: { key: "C", degrees: ["I", "IV", "V", "I"] },
    },
  ],
  u2: [
    {
      title: "minor chords too",
      body: "Not every chord is bright. The 2, 3 and 6 chords are minor — a softer, sadder sound. We write them lowercase: ii, iii, vi.",
      example: { key: "C", degrees: ["ii", "iii", "vi"] },
    },
    {
      title: "how a minor chord is built",
      body: "A minor triad flips the major recipe: root, up 3 half-steps, then up 4 (the major's middle note, dropped a half-step). Nothing new to memorize — it's one move on a chord you already know. Here's vi in C, Am: A, C, E.",
      recipe: { key: "C", deg: "vi" },
    },
    {
      title: "in C",
      body: "ii=Dm, iii=Em, vi=Am. The little 'm' means minor. vi (Am) is the famous relative minor — the same notes as C major, a different home base.",
      example: { key: "C", degrees: ["vi", "IV", "I", "V"] },
    },
  ],
  u3: [
    {
      title: "the four-chord loop",
      body: "I–V–vi–IV is the most-used progression in pop. In C: C–G–Am–F. Once your ear knows it, you'll hear it everywhere.",
      example: { key: "C", degrees: ["I", "V", "vi", "IV"] },
    },
    {
      title: "hear it, name it",
      body: "Now you'll listen to loops and name the numbers. The shape matters more than the key — same four moves, any key.",
    },
  ],
  u4: [
    {
      title: "same numbers, new key",
      body: "Move home base to G. The numbers don't change — I–IV–V is still the big three — but now I=G, IV=C, V=D.",
      example: { key: "G", degrees: ["I", "IV", "V"] },
    },
    {
      title: "build it in G",
      body: "The major-triad recipe doesn't change with the key: root, up 4 half-steps, then up 3. Here's the home chord in G — G, B, D. Same move as C, new starting note.",
      recipe: { key: "G", deg: "I" },
    },
    {
      title: "this is the payoff",
      body: "A progression you learned in C plays the same way in G, just shifted up. That portability is the whole point of numbers.",
      example: { key: "G", degrees: ["I", "V", "vi", "IV"] },
    },
  ],
  u5: [
    {
      title: "prove it in D",
      body: "Another key, same system. In D: I=D, IV=G, V=A, vi=Bm. If you can find the numbers, you can play in any key.",
      example: { key: "D", degrees: ["I", "IV", "V", "vi"] },
    },
    {
      title: "build it in D",
      body: "Same recipe again: root, up 4 half-steps, then up 3. The home chord in D is D, F#, A. Once the move is automatic, every key opens up.",
      recipe: { key: "D", deg: "I" },
    },
  ],
  u6: [
    {
      title: "the 50s turn",
      body: "I–vi–IV–V (the doo-wop loop) in A: A–F#m–D–E. A new key and a classic progression at once.",
      example: { key: "A", degrees: ["I", "vi", "IV", "V"] },
    },
    {
      title: "build it in A",
      body: "Root, up 4 half-steps, then up 3 — the home chord in A is A, C#, E. The black keys change but the recipe is identical.",
      recipe: { key: "A", deg: "I" },
    },
  ],
  u7: [
    {
      title: "train your ear",
      body: "Now mix it up: hear a loop in any key you've learned and name the numbers — no prompts, just listening.",
      example: { key: "G", degrees: ["I", "V", "vi", "IV"] },
    },
    {
      title: "songs, too",
      body: "You'll match real songs to their number progressions. This is how working musicians call tunes on the fly.",
    },
  ],
  u8: [
    {
      title: "one more note",
      body: "A seventh chord stacks one more third on a triad — a richer, jazzier color. Cmaj7, G7, Am7.",
      example: { key: "C", degrees: ["Imaj7", "V7", "vi7"] },
    },
    {
      title: "quality matters",
      body: "The 1 and 4 become maj7, the 5 a dominant 7 (just \"7\"), the minor chords m7, and the 7 is m7♭5. So in C: Imaj7=Cmaj7, V7=G7.",
      example: { key: "C", degrees: ["Imaj7", "IVmaj7", "V7", "ii7"] },
    },
  ],
  m1: [
    {
      title: "a minor world",
      body: "Minor keys feel darker. In a minor key the home chord (i) is itself minor. We're in A minor: i=Am, iv=Dm, v=Em.",
      example: { key: "Am", degrees: ["i", "iv", "v"] },
    },
    {
      title: "build the minor home",
      body: "The minor-triad recipe: root, up 3 half-steps, then up 4. The home chord in A minor is Am — A, C, E. (It's the major recipe with its middle note dropped a half-step.)",
      recipe: { key: "Am", deg: "i" },
    },
    {
      title: "lowercase home",
      body: "Notice i, iv, v are lowercase — in natural minor these three are all minor, unlike major's bright I–IV–V.",
      example: { key: "Am", degrees: ["i", "iv", "v", "i"] },
    },
  ],
  m2: [
    {
      title: "the brighteners",
      body: "Minor keys still have major chords: the 3, 6 and 7. In Am: III=C, VI=F, VII=G. They lift the mood inside a minor song.",
      example: { key: "Am", degrees: ["III", "VI", "VII"] },
    },
  ],
  m3: [
    {
      title: "the minor anthem",
      body: "i–VI–III–VII (Am–F–C–G) is the minor cousin of the four-chord loop — the sound of countless emotional pop songs.",
      example: { key: "Am", degrees: ["i", "VI", "III", "VII"] },
    },
  ],
  m4: [
    {
      title: "minor is portable too",
      body: "Same minor numbers, new home: E minor. i=Em, iv=Am, v=Bm, VI=C. The shapes move just like in major.",
      example: { key: "Em", degrees: ["i", "iv", "v", "VI"] },
    },
    {
      title: "build it in E minor",
      body: "Same minor recipe — root, up 3 half-steps, then up 4 — now from E. The home chord is Em: E, G, B.",
      recipe: { key: "Em", deg: "i" },
    },
  ],
  m5: [
    {
      title: "onward to B minor",
      body: "i=Bm, iv=Em, v=F#m, VII=A. By now the pattern should feel automatic — find the home, count the rest.",
      example: { key: "Bm", degrees: ["i", "iv", "VII", "III"] },
    },
    {
      title: "build it in B minor",
      body: "Root, up 3 half-steps, then up 4, starting on B: the home chord is Bm — B, D, F#. The move is the same; only home moved.",
      recipe: { key: "Bm", deg: "i" },
    },
  ],
  m6: [
    {
      title: "minor by ear",
      body: "Hear minor loops across keys and name the numbers. The minor flavor is unmistakable once you've drilled it.",
      example: { key: "Em", degrees: ["i", "VII", "VI", "VII"] },
    },
  ],
  vc1: [
    {
      title: "where your hands go",
      body: "The comping texture is a two-hand split: your left hand plays the root down low, your right hand plays the chord up in the middle. Left is the foundation, right is the color. Here's I in C — a low C in the left, C–E–G in the right.",
      recipe: { key: "C", deg: "I" },
    },
    {
      title: "leave room for the bass",
      body: "In a band the bassist plays the root. If you double it, the low end gets muddy. So drop the root and play only the rest of the chord — a rootless voicing.",
      example: { key: "C", degrees: ["I", "IV", "V", "vi"] },
    },
    {
      title: "rootless triads",
      body: "A triad is root–3rd–5th. Drop the root and you're left with the 3rd and 5th — two notes that still spell the chord's color. In C, I (C–E–G) becomes just E–G.",
      example: { key: "C", degrees: ["I"], voicing: true },
    },
    {
      title: "play the guide tones",
      body: "Now you'll build chords with the root left out. The drill grades the exact notes — play the root and it's wrong, on purpose. Trust the bass; you cover the color.",
    },
  ],
  vc2: [
    {
      title: "the shell of a 7th",
      body: "Seventh chords have four notes: root–3rd–5th–7th. The two that carry the sound are the 3rd and the 7th — the guide tones. Keep those, drop the root (the bass has it) and the 5th.",
      example: { key: "C", degrees: ["Imaj7", "V7", "ii7"] },
    },
    {
      title: "just 3rd & 7th",
      body: "This lean two-note shell isn't a jazz-only trick — it works in rock, alt, indie, soul, any band with a bass player: clear, and it leaves room for everyone else. In C, V7 (G–B–D–F) becomes just B–F. (Playing solo, with no bass? Then keep the root.)",
      example: { key: "C", degrees: ["ii7", "V7", "Imaj7"], voicing: true },
    },
  ],
  vc3: [
    {
      title: "same chord, different bass",
      body: "An inversion is the same chord with a different note on the bottom. C major (C–E–G) can sit with C, E, or G in the bass — root position, 1st inversion, 2nd inversion. Written C, C/E, C/G.",
      example: { key: "C", degrees: ["I", "IV", "V"] },
    },
    {
      title: "why bother",
      body: "Inversions let your hand barely move between chords — that's voice leading, and it's what makes comping sound smooth instead of jumpy. Pick the inversion whose notes are closest to the chord you just played.",
    },
    {
      title: "put the bass note on the bottom",
      body: "Now you'll play the chord with a specific note lowest. Play all three notes, but make sure the named bass note is the lowest one you press — that's what the drill checks.",
    },
  ],
};

// A "meet the scale" slide for a key (rendered with numbered note chips + a
// hear-the-scale button by the teach screen).
const scaleSlide = (key) => {
  const minor = MINOR_KEYS.includes(key);
  return {
    title: `the ${key} ${minor ? "minor" : "major"} scale`,
    body: `Before the chords, meet the scale. These seven notes are the home team for ${key} — every chord in this key is built from them, so knowing the scale is what makes the chords click.`,
    scale: { key },
  };
};

// Units that introduce a new key get that key's scale taught up front.
const UNIT_SCALE_INTRO = {
  u1: "C",
  u4: "G",
  u5: "D",
  u6: "A",
  m1: "Am",
  m4: "Em",
  m5: "Bm",
};

// Every unit by id, for plan-aware teaching lookups.
const UNIT_BY_ID = Object.fromEntries(
  [...UNITS, ...MINOR_UNITS, ...VOICING_UNITS].map((u) => [u.id, u])
);

// The "meet the chords" slide for a per-key chords lesson — its title/body adapt
// to the quality (major/minor) and whether the key itself is minor.
const chordLessonSlide = (unit, spec) => {
  const minorKey = MINOR_KEYS.includes(unit.key);
  const major = spec.quality === "major";
  const chords = (spec.degrees || []).map((d) => chordFor(unit.key, d)).join(", ");
  // A seventh-chord lesson carries its own title/body via the spec.
  if (spec.seventh) {
    const isMin7 = (spec.degrees || [])[0] && spec.degrees[0].startsWith("ii");
    return {
      title: spec.name || "seventh chords",
      body: isMin7
        ? `The minor chords each take a minor 7th on top: m7. In ${unit.key} that's ${chords} — smoother and a touch more wistful than the plain triads.`
        : `A seventh chord adds one more note on top of the triad. The 1 and 4 become major 7ths, and the 5 becomes a dominant 7 (just "7"). In ${unit.key}: ${chords}.`,
      example: { key: unit.key, degrees: spec.degrees },
    };
  }
  let title;
  let body;
  if (!minorKey && major) {
    title = "the major chords";
    body = `The 1, 4 and 5 chords are major — the bright, stable ones. In ${unit.key} that's ${chords}. Thousands of songs live on just these three.`;
  } else if (!minorKey) {
    title = "the minor chords";
    body = `The 2, 3 and 6 chords are minor — softer and sadder. In ${unit.key}: ${chords}. The lowercase numbers (ii, iii, vi) mark them minor.`;
  } else if (!major) {
    title = "the home chords";
    body = `In a minor key the home chords are themselves minor: i, iv and v. In ${unit.key} that's ${chords} — the dark heart of the key.`;
  } else {
    title = "the brighteners";
    body = `Minor keys still have major chords — the 3, 6 and 7. In ${unit.key}: ${chords}. They lift the mood inside a minor song.`;
  }
  return { title, body, example: { key: unit.key, degrees: spec.degrees } };
};

// The teaching slides for a progressions lesson. The first key just introduces
// the idea + its loop; every later key opens by replaying a loop you already
// learned — now in this key, to show portability — then introduces the new one.
const progTeachSlides = (unit, spec) => {
  const key = unit.key;
  const all =
    spec.progs && spec.progs.length ? spec.progs : [unit.prog].filter(Boolean);
  const fresh = all[all.length - 1] || [];
  const prior = all.slice(0, -1);
  // The sevenths unit isn't a key-building sequence — lead with the ii–V–I.
  if (unit.seventh) {
    return [
      {
        title: "the ii–V–I",
        body: `The most important progression in jazz: ii7 → V7 → Imaj7. In ${key} that's ${progChords(key, ["ii7", "V7", "Imaj7"])}. Once your ear knows it, you'll hear it everywhere.`,
        example: { key, degrees: ["ii7", "V7", "Imaj7"] },
      },
    ];
  }
  if (!prior.length) {
    return [
      {
        title: "chord progressions",
        body: `Now put the chords in motion. A progression is a short loop of numbers — and because it's numbers, the same loop plays in any key. Here's your first one in ${key}: ${degStr(fresh)}.`,
        example: { key, degrees: fresh },
      },
    ];
  }
  const known = prior[prior.length - 1]; // the loop the previous key added
  return [
    {
      title: "you already know this one",
      body: `Same numbers, new key. You learned ${degStr(known)} earlier — here it is in ${key}, nothing to relearn. Every progression you pick up travels like this.`,
      example: { key, degrees: known },
    },
    {
      title: "now add a new one",
      body: `And here's the new progression for ${key}: ${degStr(fresh)}. Learn it here and it works in every key too.`,
      example: { key, degrees: fresh },
    },
  ];
};

// The teaching slides for one lesson of a per-key (plan) unit.
const planLessonTeach = (unit, spec) => {
  const key = unit.key;
  if (!spec) return [];
  if (spec.kind === "scale") {
    return [
      scaleSlide(key),
      {
        title: "number the notes",
        body: `Count up the scale and every note gets a number — 1 is home (${(scaleFor(key) || [])[0]}), then 2, 3, 4 … up to 7. Those numbers are how every chord in ${key} gets its name.`,
      },
    ];
  }
  if (spec.kind === "chords") {
    const seventh = Boolean(spec.seventh);
    const major = spec.quality === "major";
    const howTitle = seventh
      ? "how a seventh is built"
      : major
      ? "how a major chord is built"
      : "how a minor chord is built";
    const howBody = seventh
      ? "A seventh chord stacks one more third on top of the triad — four notes instead of three, for a richer, jazzier color."
      : major
      ? "A major triad stacks the root, up 4 half-steps, then up 3 — one rule from any starting note."
      : "A minor triad stacks the root, up 3 half-steps, then up 4 — the major shape with its middle note a half-step lower.";
    // Teach (and let them hear) every chord before any questions: an overview,
    // then one build slide per chord.
    const perChord = (spec.degrees || []).map((deg, i) => {
      const chord = chordFor(key, deg);
      return {
        title: i === 0 ? howTitle : `next: the ${deg} chord (${chord})`,
        body:
          i === 0
            ? `${howBody} Start with the ${deg} chord, ${chord} — build it, then hear it.`
            : `${seventh ? "Another seventh" : "Same move from a new root"} — the ${deg} chord is ${chord}. Build it on the keys, then hear it.`,
        recipe: { key, deg },
      };
    });
    return [chordLessonSlide(unit, spec), ...perChord];
  }
  if (spec.kind === "prog") {
    return [
      ...progTeachSlides(unit, spec),
    ];
  }
  // ---- voicing-track lessons ----
  if (spec.kind === "voicing") {
    if (spec.seventh) {
      return [
        {
          title: "the shell of a 7th",
          body: "A seventh chord has four notes: root–3rd–5th–7th. The two that carry the sound are the 3rd and the 7th — the guide tones. Keep those, drop the root (the bass has it) and the 5th.",
          example: { key, degrees: ["Imaj7", "V7", "ii7"] },
        },
        {
          title: "just 3rd & 7th",
          body: "This lean two-note shell works in any band with a bass player — clear, and it leaves room for everyone else. In C, V7 (G–B–D–F) becomes just B–F.",
          example: { key, degrees: ["ii7", "V7", "Imaj7"], voicing: true },
        },
        {
          title: "build the shells",
          body: "Now build the guide tones across the diatonic 7ths — the drill grades the 3rd & 7th only, in a few keys, so the shape travels.",
        },
      ];
    }
    return [
      {
        title: "where your hands go",
        body: "On your own, comping is a two-hand split: your left hand plays the root down low, your right hand plays the chord up in the middle. But in a band the bassist already owns that low root — doubling it just muddies the low end. So leave the root to the bass and play only the upper notes of the chord. That's a rootless voicing.",
        recipe: { key, deg: "I" },
      },
      {
        title: "rootless triads",
        body: "A triad is root–3rd–5th. Drop the root and you're left with the 3rd and 5th — two notes that still spell the chord's color. In C, I (C–E–G) becomes just E–G.",
        example: { key, degrees: ["I"], voicing: true },
      },
      {
        title: "play the guide tones",
        body: "Now you'll build chords with the root left out, in a few keys. The drill grades the exact notes — play the root and it's wrong, on purpose. Trust the bass; you cover the color.",
      },
    ];
  }
  if (spec.kind === "comp") {
    const loop = spec.prog || unit.prog || [];
    return [
      {
        title: "comp the changes",
        body: spec.seventh
          ? "Now play the shells through a whole progression — over a ii–V–I the guide tones barely move, gliding from chord to chord. Play each chord's 3rd & 7th in turn, in time."
          : "Now play the shells through a whole progression — the actual comping move. Play each chord's guide tones, skip the root, and keep the loop going.",
        example: { key, degrees: loop, voicing: true },
      },
    ];
  }
  if (spec.kind === "inversion") {
    return [
      {
        title: "same chord, different bass",
        body: "An inversion is the same chord with a different note on the bottom. C major (C–E–G) can sit with C, E, or G in the bass — root position, 1st inversion, 2nd inversion. Written C, C/E, C/G.",
        example: { key, degrees: ["I", "IV", "V"] },
      },
      {
        title: "put the bass note on the bottom",
        body: "Now you'll play the chord with a specific note lowest. Play all three notes, but make sure the named bass note is the lowest one you press — that's what the drill checks.",
      },
    ];
  }
  if (spec.kind === "voicelead") {
    if (spec.shell) {
      return [
        {
          title: "the 3rd becomes the 7th",
          body: "Here's the magic of shells through a ii–V–I: one guide tone holds while the other slides a half-step — the 3rd of one chord becomes the 7th of the next. In C: Dm7 is F–C, then G7 keeps the F and drops C to B, then Cmaj7 keeps the B and drops F to E. Your hand barely moves and the changes still speak.",
          example: { key, degrees: spec.prog || [], voicing: true },
        },
        {
          title: "smallest move wins",
          body: "The drill takes the shell either way up — what it scores is how far your hand travels. Hold the common tone, slide the other, and watch the move count.",
        },
      ];
    }
    return [
      {
        title: "voice leading",
        body: "Inversions let your hand barely move between chords — that's voice leading, and it's what makes comping sound smooth instead of jumpy. First, spot the note two chords share: that's the one a smooth hand holds onto.",
        example: { key, degrees: spec.prog || unit.prog || [] },
      },
      {
        title: "any inversion — smallest move wins",
        body: "From here the drill doesn't dictate an inversion. Play each chord any way up; it measures how far you moved from the last chord and shows you the shortest path. Right chord + short trip is the skill.",
      },
    ];
  }
  if (spec.kind === "color") {
    return [
      {
        title: "sus chords: tension you can hold",
        body: "A sus chord swaps the 3rd out — sus4 lifts it to the 4th, sus2 drops it to the 2nd. Without a 3rd it's neither major nor minor, just suspended… until the swapped note settles back and the chord relaxes. Csus4 (C–F–G) melting into C (C–E–G) is half of every ballad chorus.",
        example: { key, degrees: ["I", "V"] },
      },
      {
        title: "add9: the shimmer",
        body: "add9 keeps the whole triad and stacks the 2nd on top — Cadd9 is C–D–E–G. It's the modern pop / worship default color for I and IV. One caution: if the singer is sitting on that note, leave it out. Color is seasoning, not the meal.",
      },
      {
        title: "the money move",
        body: "Vsus4 → V → I: hold the suspension, release it, land home — the last-chorus move you've heard a thousand times. You'll build each color, then play every release, ending with the sus inside a real loop.",
      },
    ];
  }
  if (spec.kind === "walk") {
    return [
      {
        title: "slash chords have a job",
        body: "A slash chord is just an inversion — but in a band it's bass motion. I → I/3 → IV walks the bass up by scale steps (C → C/E → F): smooth, purposeful, and everywhere in pop and gospel.",
        example: { key, degrees: ["I", "IV"] },
      },
      {
        title: "the walk-down",
        body: "The same trick descending: I → V/3 → vi (C → G/B → Am) steps the bass down one scale note at a time — the ballad walk-down. The drill grades the lowest note you press, because the bass line is the whole point.",
      },
    ];
  }
  if (spec.kind === "space") {
    return [
      {
        title: "the notes you don't play",
        body: "The number-one thing that makes a keys player welcome in a band is restraint. When the drummer fills, the singer takes a pickup, or the guitar has its moment — lay out. Playing through someone else's bar is the classic beginner tell, and no amount of good voicings fixes it.",
      },
      {
        title: "hear the cue",
        body: "You'll know the bar: the snare starts rolling and the bass drops out — that's a fill, and it's your cue to lift your hands. If full silence feels too naked at first, the half-comp is the intermediate dynamic on a real gig: left hand only, thin and low. In this drill, though, the answer is hands off.",
      },
      {
        title: "silence is graded",
        body: "Comp the pattern for three bars, then the chart says rest — any note you strike in that bar counts against you, and laying out cleanly counts as nailing it. This is the one drill where extra notes are not forgiven. That's the point.",
      },
    ];
  }
  return [];
};

// The teaching slides for a whole unit (the Learn library + the home "learn"
// button): a plan unit concatenates its lessons' slides; older units use the
// TEACH map, leading with a scale slide when they introduce a key.
export const unitTeach = (id) => {
  const u = UNIT_BY_ID[id];
  if (u && u.plan) return u.plan.flatMap((spec) => planLessonTeach(u, spec));
  const base = TEACH[id];
  if (!base) return null;
  const key = UNIT_SCALE_INTRO[id];
  return key ? [scaleSlide(key), ...base] : base;
};

// The teaching slides shown before a single lesson: per-lesson for plan units,
// and (as before) the unit intro on the first lesson of an older unit.
export const lessonTeach = (unit, idx) => {
  if (!unit) return null;
  if (unit.plan) return planLessonTeach(unit, unit.plan[idx]);
  return idx === 0 ? unitTeach(unit.id) : null;
};

// The scale lesson for a key, for the Learn library's "scales" section: meet
// the notes, then see the chords built from them.
export const scaleTeach = (key) => [
  scaleSlide(key),
  {
    title: "chords come from the scale",
    body: "Build a chord by landing on a number and stacking every other note: 1–3–5 is the home chord, the next is 4–6–8, and so on. That's why every chord in a key shares the same seven notes.",
    example: { key, degrees: MINOR_KEYS.includes(key) ? ["i", "iv", "v"] : ["I", "IV", "V"] },
  },
];

// The keys offered in the Learn library's scales section (majors then minors).
export const SCALES = [...KEYS, ...MINOR_KEYS].map((key) => ({
  id: `scale-${key}`,
  key,
  title: `${key} ${MINOR_KEYS.includes(key) ? "minor" : "major"}`,
}));

/* Standalone concept cards for the Learn library — the cross-cutting ideas that
   aren't tied to a single unit. Shown above the per-unit entries. */
export const FOUNDATIONS = [
  {
    id: "f-nns",
    title: "the number system",
    teach: [
      {
        title: "what it is",
        body: "Instead of chord letters, name each chord by its number in the key. The Nashville Number System lets musicians write and call songs in every key at once.",
      },
      {
        title: "why numbers win",
        body: "Letters change with the key; numbers don't. \"1–5–6–4\" is the same song-shape whether you play it in C, in G, or anywhere else.",
        example: { key: "C", degrees: ["I", "V", "vi", "IV"] },
      },
    ],
  },
  {
    id: "f-scales",
    title: "scales: where chords come from",
    teach: [
      {
        title: "the home team",
        body: "A key is built from a seven-note scale. Every chord in that key is just notes from the scale stacked up — so once you know the scale, the chords stop looking like random shapes.",
        scale: { key: "C" },
      },
      {
        title: "stack every other note",
        body: "Start on any scale note and stack every other one: 1+3+5 is the I chord, 2+4+6 is the ii chord. Same seven notes, different starting points.",
        example: { key: "C", degrees: ["I", "ii", "iii"] },
      },
    ],
  },
  {
    id: "f-chords",
    title: "building a chord",
    teach: [
      {
        title: "the triad recipe",
        body: "A basic chord is a triad — three notes. A major triad is one rule: start on the root, go up 4 half-steps, then up 3. Learn the rule, not twelve separate chords.",
        recipe: { key: "C", deg: "I" },
      },
      {
        title: "minor: drop the middle",
        body: "A minor triad is the same shape with the middle note a half-step lower — up 3, then up 4. No new memorizing: it's a single move on a major chord you already have.",
        recipe: { key: "C", deg: "vi" },
      },
    ],
  },
  {
    id: "f-reading",
    title: "reading the numbers",
    teach: [
      {
        title: "case and symbols",
        body: "UPPERCASE = major (I, IV, V). lowercase = minor (ii, iii, vi). A little ° means diminished (vii°), and a 7 means a seventh chord (V7).",
        example: { key: "C", degrees: ["I", "ii", "vii°", "V7"] },
      },
    ],
  },
];

/* ---------------- pure helpers ---------------- */
export const shuffle = (a) => {
  a = [...a];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const sample = (arr, n, not = []) =>
  shuffle(arr.filter((x) => !not.includes(x))).slice(0, n);

export const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

/* ---------------- progress helpers (streak / xp) ----------------
   Pure functions the page uses to update saved progress, kept here so they're
   unit-testable without the React component. A "day key" is the `YYYY-M-D`
   string the page stores in `lastDate` (see todayKey in NumberSense.js). */

// Parse a day key ("2026-6-24") into a local Date at midnight.
export const parseDayKey = (key) => {
  const [y, m, d] = String(key).split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Whole calendar days from day key `b` to day key `a` (a − b), or null if
// either is missing / unparseable.
export const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const da = parseDayKey(a);
  const db = parseDayKey(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
  return Math.round((da - db) / 86400000);
};

// The streak after finishing a lesson today, given the previously recorded
// play-day. Same day → unchanged; the very next day → +1; any gap (or the
// first ever play) → reset to 1. Fixes the old counter that only ever climbed
// and never reset on a missed day.
export const nextStreak = (prevStreak, lastDate, today) => {
  const prev = prevStreak || 0;
  if (lastDate === today) return prev || 1;
  if (daysBetween(today, lastDate) === 1) return prev + 1;
  return 1;
};

// XP for passing a lesson: a base award plus a bonus for a perfect (no-heart-
// lost) run.
export const lessonXp = (hearts, maxHearts = 5) =>
  12 + (hearts === maxHearts ? 8 : 0);

// The lesson runtime after grading one answer (pure — the screen just stores
// the result). `hearts` reaching 0 ends the lesson; the component drives that.
export const gradeStep = (state, isCorrect) => ({
  total: (state.total || 0) + 1,
  correct: (state.correct || 0) + (isCorrect ? 1 : 0),
  hearts: (state.hearts || 0) - (isCorrect ? 0 : 1),
});

// How many rounds each practice game runs (the Translator is time-boxed, not
// round-boxed, so it isn't listed here).
export const GAME_ROUNDS = { builder: 6, ear: 6, song: 5, tonic: 6, cadence: 6, predict: 6 };

// XP a finished game awards for its score. The Translator pays 1 XP per correct
// answer; the slower round-based games pay double so a good run is worth a
// comparable amount.
export const gameXp = (kind, score) => (kind === "translate" ? score : score * 2);

/* ---------------- user settings (audio) ----------------
   Small, persisted-alongside-progress preferences for how playback feels:
   a global mute, the ear-training tempo (stored as ms-per-beat, the unit the
   audio engine and PlayButton already speak), and an optional count-in click
   before loops, plus the calibrated MIDI keyboard range (settings → "map my
   keyboard") that octave-shifts a small or shifted keyboard onto the screen
   piano. `normalizeSettings` clamps/validates whatever comes back from
   storage so a malformed doc can never break playback. */
export const TEMPO_MIN = 380; // ms per beat — fastest (~158 bpm)
export const TEMPO_MAX = 1000; // ms per beat — slowest (60 bpm)
export const DEFAULT_SETTINGS = {
  muted: false,
  tempo: 720,
  countIn: false,
  midiLow: null,
  midiHigh: null,
};

// A valid MIDI note number, or null.
const midiNoteOrNull = (v) => (Number.isInteger(v) && v >= 0 && v <= 127 ? v : null);

export const normalizeSettings = (raw = {}) => {
  const low = midiNoteOrNull(raw && raw.midiLow);
  const high = midiNoteOrNull(raw && raw.midiHigh);
  // The range only means anything as an ordered pair — a half-set or
  // inverted range normalizes back to "not calibrated".
  const rangeOk = low != null && high != null && low < high;
  return {
    muted: Boolean(raw && raw.muted),
    tempo:
      raw && Number.isFinite(raw.tempo)
        ? Math.min(TEMPO_MAX, Math.max(TEMPO_MIN, Math.round(raw.tempo)))
        : DEFAULT_SETTINGS.tempo,
    countIn: Boolean(raw && raw.countIn),
    midiLow: rangeOk ? low : null,
    midiHigh: rangeOk ? high : null,
  };
};

// The tempo slider speaks BPM; the engine speaks ms-per-beat. These convert.
export const beatMsToBpm = (ms) => Math.round(60000 / ms);
export const bpmToBeatMs = (bpm) => Math.round(60000 / bpm);

/* ---------------- cross-device recovery ----------------
   Progress lives in Firestore at numberSense/{userId}, where userId is the
   anonymous per-device id. "Recovery" is just carrying that id to another
   device: surface it as a copyable code, and accept a pasted code to re-bind.
   This normalizes a pasted code, returning the cleaned id or null if it doesn't
   look like one (a uuid or the `${timestamp}-${rand}` fallback shape). */
export const normalizeRecoveryCode = (raw) => {
  const code = String(raw || "").trim();
  return /^[A-Za-z0-9-]{8,}$/.test(code) ? code : null;
};

/* ---------------- per-concept mastery (spaced repetition) ----------------
   `completed` is binary — a lesson is done forever. Mastery tracks *strength*
   per (key, degree) concept so weak ones can be resurfaced for review. The
   persisted shape (alongside xp/streak/completed) is:

     mastery: { "C:IV": { strength, seen, correct }, ... }

   `strength` is a 0..MASTERY_MAX Leitner-style box: a right answer promotes one
   box, a wrong answer demotes one. New / never-drilled concepts read as 0 (the
   weakest), so they surface first. All pure so the page and games can fold
   graded answers in without touching React.

   Fluency: band playing is a speed skill, so promotion also requires the
   answer to be *fast* — a correct-but-slow answer holds its box instead of
   climbing (a graded result may carry `ms`, and `type` to pick the threshold,
   or an explicit `fast` flag from timed play). Entries also remember `last`
   (the day key of the last drill) and a rolling `ms`, so strength can decay
   once a box outlives its freshness window (see decayedStrength). */
export const MASTERY_MAX = 5;

// How long an answer can take and still count as fluent, per question type
// (ms). Generous on purpose — this is "without counting", not "instant".
export const FAST_MS = {
  translate: 5000,
  scalenote: 5000,
  complete: 8000,
  build: 12000,
  inversion: 12000,
  default: 10000,
};

// Is a graded answer fast enough to promote? No recorded time (older screens,
// batch imports) counts as fast so legacy results keep their old behavior.
export const isFast = (type, ms) =>
  ms == null || ms <= (FAST_MS[type] || FAST_MS.default);

// The next strength after a graded answer (clamped to 0..MASTERY_MAX). Wrong
// demotes; right-and-fast promotes; right-but-slow holds the box.
export const bumpStrength = (prev, correct, fast = true) => {
  const s = prev || 0;
  if (!correct) return Math.max(0, s - 1);
  return fast ? Math.min(MASTERY_MAX, s + 1) : s;
};

/* ---- decay: a box is only as strong as it is fresh ----
   Each Leitner box stays fresh for a bounded window; past it the concept reads
   one box weaker, then keeps sliding down window by window. That's what makes
   this *spaced* repetition — mastered concepts come due instead of vanishing
   from review forever. Entries with no `last` never decay (legacy data). */
export const BOX_FRESH_DAYS = [1, 1, 2, 4, 8, 16];

export const decayedStrength = (strength, last, today) => {
  let s = strength || 0;
  if (!s || !last || !today) return s;
  let d = daysBetween(today, last);
  if (d == null) return s;
  while (s > 0 && d > BOX_FRESH_DAYS[s]) {
    d -= BOX_FRESH_DAYS[s];
    s -= 1;
  }
  return s;
};

// The canonical concept id for a (key, degree) pair, e.g. { key:"C", deg:"IV" }.
export const conceptKey = ({ key, deg }) => `${key}:${deg}`;

// The ear dimension of a concept — hearing C's V is tracked apart from
// knowing it on paper, so a weak ear resurfaces on its own schedule.
export const earConceptId = (key, deg) => `${key}:${deg}:ear`;

// The shell dimension — knowing a chord's rootless voicing is a different
// skill from knowing the chord, so it gets its own Leitner box too. A strong
// C:V from triad drilling must not hide a shell you can't play.
export const shellConceptId = (key, deg) => `${key}:${deg}:shell`;

// The concept a graded question exercises, or null when it isn't a single-
// concept question. Translate/build carry a plain (key, deg) — except shell
// builds (voicingQ), which grade the shell dimension; the single-answer ear
// questions (earmove / predict) grade the ear dimension. Loop ear questions
// stay null here — they're graded slot-by-slot by the screen (see
// earSlotResults); song/progression span a whole loop and aren't tracked.
export const questionConcept = (q) => {
  if (!q || !q.key || !q.deg) return null;
  if (q.type === "build")
    return q.voicing ? shellConceptId(q.key, q.deg) : `${q.key}:${q.deg}`;
  if (q.type === "translate") return `${q.key}:${q.deg}`;
  if (q.type === "earmove" || q.type === "predict")
    return earConceptId(q.key, q.deg);
  return null;
};

// Fold an array of { concept, correct, ms?, type?, fast? } graded results into
// a mastery map, returning a new map (never mutates the input). Results
// without a concept (e.g. ear/song questions) are skipped. `today` (a day key)
// stamps each touched entry's `last` so decay has something to measure from.
export const applyResults = (mastery, results = [], today = null) => {
  const next = { ...(mastery || {}) };
  results.forEach(({ concept, correct, ms, type, fast }) => {
    if (!concept) return;
    const prev = next[concept] || { strength: 0, seen: 0, correct: 0 };
    const entry = {
      ...prev,
      strength: bumpStrength(
        prev.strength,
        correct,
        fast != null ? fast : isFast(type, ms)
      ),
      seen: prev.seen + 1,
      correct: prev.correct + (correct ? 1 : 0),
    };
    // rolling response time — a light exponential average, only when measured
    if (ms != null)
      entry.ms = prev.ms == null ? Math.round(ms) : Math.round(prev.ms * 0.7 + ms * 0.3);
    if (today) entry.last = today;
    next[concept] = entry;
  });
  return next;
};

// A concept's current strength (0 when never drilled). Pass `today` to read
// the *effective* strength — the stored box, decayed by staleness.
export const conceptStrength = (mastery, concept, today = null) => {
  const m = mastery && mastery[concept];
  if (!m) return 0;
  return today ? decayedStrength(m.strength || 0, m.last, today) : m.strength || 0;
};

// How heavily to weight a concept for review — weakest get the most weight.
export const conceptWeight = (strength) => MASTERY_MAX + 1 - (strength || 0);

// The `n` weakest concepts among `pairs` (weakest strength first; ties keep
// input order so the result is stable for a given mastery snapshot). With
// `today`, staleness counts — a decayed box sorts as weak as it now reads.
export const weakestConcepts = (pairs = [], mastery = {}, n = pairs.length, today = null) =>
  pairs
    .map((p, i) => ({ p, i, s: conceptStrength(mastery, conceptKey(p), today) }))
    .sort((a, b) => a.s - b.s || a.i - b.i)
    .slice(0, Math.max(0, n))
    .map((x) => x.p);

// Concepts that have gone stale: drilled before, but sitting past their box's
// freshness window. These are what the path should resurface for review.
export const conceptsDue = (pairs = [], mastery = {}, today = null) =>
  pairs.filter((p) => {
    const m = mastery && mastery[conceptKey(p)];
    if (!m || !m.seen || !m.strength) return false;
    return decayedStrength(m.strength, m.last, today) < m.strength;
  });

/* ---------------- stats & history (for the stats screen) ----------------
   A lightweight, append-only activity log persisted alongside progress:

     history: { "2026-6-24": { xp, lessons }, ... }

   keyed by the same `YYYY-M-D` day key as `lastDate`. It accumulates XP earned
   and lessons completed per day so the stats screen can chart a trend without
   storing a per-event log. Accuracy breakdowns are derived from the existing
   per-concept `mastery` map (seen / correct counts) — nothing new to persist.
   All pure so they're unit-testable away from React. */

// Add a day's activity to the history map, returning a new map (never mutates).
export const recordHistory = (history, date, { xp = 0, lessons = 0 } = {}) => {
  const next = { ...(history || {}) };
  if (!date) return next;
  const prev = next[date] || { xp: 0, lessons: 0 };
  next[date] = { xp: prev.xp + xp, lessons: prev.lessons + lessons };
  return next;
};

// History as a chronological array with a running XP total, oldest first:
//   [{ date, xp, lessons, cumXp }]
export const historySeries = (history = {}) => {
  let cum = 0;
  return Object.keys(history || {})
    .sort((a, b) => parseDayKey(a) - parseDayKey(b))
    .map((date) => {
      const { xp = 0, lessons = 0 } = history[date] || {};
      cum += xp;
      return { date, xp, lessons, cumXp: cum };
    });
};

const splitConcept = (id) => {
  const [key, deg, dim] = String(id).split(":");
  return { key, deg, ear: dim === "ear", shell: dim === "shell" };
};

// A dimension's sort rank: the plain concept first, then its ear, then its shell.
const dimRank = (c) => (c.ear ? 1 : c.shell ? 2 : 0);

// Per-concept accuracy/strength derived from mastery, ordered by key then
// degree (ear/shell-dimension entries sort after the plain concept they extend).
//   [{ concept, key, deg, ear, shell, seen, correct, pct, strength }]
export const conceptStats = (mastery = {}) =>
  Object.keys(mastery || {})
    .map((id) => {
      const { key, deg, ear, shell } = splitConcept(id);
      const m = mastery[id] || {};
      const seen = m.seen || 0;
      const correct = m.correct || 0;
      return {
        concept: id,
        key,
        deg,
        ear,
        shell,
        seen,
        correct,
        pct: seen ? Math.round((correct / seen) * 100) : 0,
        strength: m.strength || 0,
        ms: m.ms != null ? m.ms : null,
      };
    })
    .sort(
      (a, b) =>
        ALL_KEYS.indexOf(a.key) - ALL_KEYS.indexOf(b.key) ||
        ALL_DEGREES.indexOf(a.deg) - ALL_DEGREES.indexOf(b.deg) ||
        dimRank(a) - dimRank(b)
    );

// Per-key accuracy roll-up, ordered by KEYS (only keys with drilled concepts).
//   [{ key, seen, correct, pct, concepts, mastered }]
export const keyStats = (mastery = {}) => {
  const by = {};
  conceptStats(mastery).forEach((c) => {
    // the key rows read as "chords you know" — ear and shells have their own blocks
    if (c.ear || c.shell) return;
    const k =
      by[c.key] ||
      (by[c.key] = {
        key: c.key,
        seen: 0,
        correct: 0,
        concepts: 0,
        mastered: 0,
        msSum: 0,
        msCount: 0,
      });
    k.seen += c.seen;
    k.correct += c.correct;
    k.concepts += 1;
    if (c.strength >= MASTERY_MAX) k.mastered += 1;
    if (c.ms != null) {
      k.msSum += c.ms;
      k.msCount += 1;
    }
  });
  return ALL_KEYS.filter((k) => by[k]).map((k) => {
    const { msSum, msCount, ...s } = by[k];
    return {
      ...s,
      pct: s.seen ? Math.round((s.correct / s.seen) * 100) : 0,
      avgMs: msCount ? Math.round(msSum / msCount) : null,
    };
  });
};

// Overall mastery totals across every drilled concept.
export const masteryTotals = (mastery = {}) => {
  const cs = conceptStats(mastery);
  const seen = cs.reduce((n, c) => n + c.seen, 0);
  const correct = cs.reduce((n, c) => n + c.correct, 0);
  return {
    tracked: cs.length,
    mastered: cs.filter((c) => c.strength >= MASTERY_MAX).length,
    seen,
    correct,
    pct: seen ? Math.round((correct / seen) * 100) : 0,
  };
};

// Look up the chord symbol / notes for a (key, degree). Thin wrappers over
// DIATONIC so the rest of this file never indexes it directly.
export const entryFor = (key, deg) =>
  tableFor(key, deg).find((c) => c.deg === deg) || null;
export const chordFor = (key, deg) => {
  const e = entryFor(key, deg);
  return e ? e.chord : deg;
};
export const degStr = (degrees) => degrees.join("–");
const progChords = (key, degrees) => degrees.map((d) => chordFor(key, d)).join("–");

// A human-readable name for a chord symbol ("Am" -> "A minor", "F" -> "F major",
// "Cmaj7" -> "C major 7", "G7" -> "G dominant 7", "Bm7♭5" -> "B half-diminished
// 7"). Seventh suffixes are checked before the plainer triad ones.
export const chordLabel = (sym) => {
  if (sym.endsWith("maj7")) return `${sym.slice(0, -4)} major 7`;
  if (sym.endsWith("m7♭5")) return `${sym.slice(0, -4)} half-diminished 7`;
  if (sym.endsWith("m7")) return `${sym.slice(0, -2)} minor 7`;
  if (sym.endsWith("7")) return `${sym.slice(0, -1)} dominant 7`;
  if (sym.endsWith("°")) return `${sym.slice(0, -1)} diminished`;
  if (sym.endsWith("m")) return `${sym.slice(0, -1)} minor`;
  return `${sym} major`;
};

/* ---------------- chord construction (the "how to play it" layer) ----------
   The trainer asks learners to *build* chords on a piano, but the curriculum
   only ever taught the number↔chord mapping, never how a chord is physically
   stacked. These pure helpers close that gap: the interval recipe behind a
   chord (for the teach slides) and a one-line build hint (for the lesson). */

// The half-step gaps between consecutive notes of a chord, e.g. C–E–G → [4, 3]
// (a major triad), A–C–E → [3, 4] (a minor triad). Wraps the octave so a
// voicing that crosses C still reads as a positive climb.
export const intervalSteps = (notes = []) =>
  notes
    .slice(1)
    .map((n, i) => ((SEMITONES[n] - SEMITONES[notes[i]]) % 12 + 12) % 12);

// The construction recipe for a (key, degree): the chord's notes, the half-step
// gaps that stack them, its root, and a plain-English quality label. Powers the
// teach slides' "how a chord is built" view and the in-lesson build hint.
export const chordRecipe = (key, deg) => {
  const e = entryFor(key, deg);
  if (!e) return null;
  return {
    key,
    deg,
    chord: e.chord,
    label: chordLabel(e.chord),
    notes: e.notes,
    root: e.notes[0],
    steps: intervalSteps(e.notes),
  };
};

// A one-line "how do I build this?" hint for a build question. It reveals the
// construction *method* (the recipe), not just the literal notes — the prompt
// already names the chord, so the missing skill is turning that name into keys.
export const buildHint = (q) => {
  if (!q) return "";
  if (q.scale) {
    const scale = scaleFor(q.key) || [];
    return `all seven scale notes, from ${scale[0]}: ${scale.join("–")}`;
  }
  if (q.color) {
    const move =
      q.color === "sus4"
        ? "take the triad and lift the 3rd up to the 4th"
        : q.color === "sus2"
        ? "take the triad and drop the 3rd down to the 2nd"
        : "keep the whole triad and add the 2nd (the 9) beside the root";
    return `${move}: ${q.targetNotes.join("–")}`;
  }
  if (q.voicing) {
    const e = entryFor(q.key, q.deg);
    const shell = shellVoicing(q.key, q.deg);
    const which = e && e.notes.length >= 4 ? "3rd & 7th" : "3rd & 5th";
    return `the guide tones (${which}) — skip the root: ${shell.join("–")}`;
  }
  const r = chordRecipe(q.key, q.deg);
  if (!r) return "";
  const stepText = r.steps.map((s) => `+${s}`).join(", then ");
  const spell = r.notes.length >= 4 ? "1·3·5·7" : "1·3·5";
  return `start on ${r.root}, go up ${stepText} half-steps (${spell}) — ${r.notes.join("–")}`;
};

const songByTitle = (title) => STARTER_SONGS.find((s) => s.title === title);

// Songs split by key type: major-track lessons only quiz major-key songs —
// the natural-minor numerals belong to the minor track.
const MAJOR_SONGS = STARTER_SONGS.filter((s) => !DIATONIC_MINOR[s.key]);
const MINOR_SONGS = STARTER_SONGS.filter((s) => DIATONIC_MINOR[s.key]);
const songPoolFor = (unit) =>
  unit && unit.minor && MINOR_SONGS.length ? MINOR_SONGS : MAJOR_SONGS;

// Three distinct progression-string distractors that aren't `correct`, drawn
// from the given pool (major loops by default, minor loops for minor keys).
const progDistractors = (correct, toStr, pool = COMMON_PROGS) => {
  const out = [];
  for (const p of shuffle(pool)) {
    const s = toStr(p);
    if (s !== correct && !out.includes(s)) out.push(s);
    if (out.length === 3) break;
  }
  return out;
};

/* ---------------- question generators ----------------
   Choice questions share the shape { type, ..., correct, options }.
   The `build` question is special (validated on an on-screen piano). */

// A few interchangeable phrasings per direction so a lesson doesn't read like
// the same sentence on repeat. `pick` chooses one when the question is built.
const NUM2CHORD_PROMPTS = [
  (key, deg) => `In ${key}, what chord is the ${deg}?`,
  (key, deg) => `${key}: which chord is the ${deg}?`,
  (key, deg) => `Name the ${deg} chord in ${key}.`,
];
const CHORD2NUM_PROMPTS = [
  (key, deg, chord) => `In ${key}, which number is ${chord}?`,
  (key, deg, chord) => `${key}: what's the number for ${chord}?`,
  (key, deg, chord) => `${chord} is which number in ${key}?`,
];

const num2chordQ = (key, deg) => {
  const correct = chordFor(key, deg);
  const distract = sample(
    tableFor(key, deg).map((c) => c.chord),
    3,
    [correct]
  );
  return {
    type: "translate",
    dir: "num2chord",
    key,
    deg,
    prompt: pick(NUM2CHORD_PROMPTS)(key, deg),
    correct,
    options: shuffle([correct, ...distract]),
  };
};

// Chord→number is honest recall, not multiple choice: the options are the
// key's whole degree row (all seven), so process-of-elimination can't carry it.
const chord2numQ = (key, deg) => {
  const pool = isSeventh(deg)
    ? SEVENTH_DEGREES
    : isMinorDeg(deg)
    ? MINOR_DEGREES
    : DEGREES;
  const chord = chordFor(key, deg);
  return {
    type: "translate",
    dir: "chord2num",
    key,
    deg,
    chord,
    prompt: pick(CHORD2NUM_PROMPTS)(key, deg, chord),
    correct: deg,
    options: shuffle([...pool]),
  };
};

const buildQ = (key, deg) => {
  const e = entryFor(key, deg);
  return {
    type: "build",
    key,
    deg,
    chord: e.chord,
    label: chordLabel(e.chord),
    targetNotes: e.notes,
  };
};

// A voicing question — a build question whose target is the *shell* (guide
// tones), root removed. Because the build screen grades by exact pitch-class
// set, playing the root fails: that's what enforces "leave the root for the
// bassist." Triads → 3rd & 5th, sevenths → 3rd & 7th (see shellVoicing).
export const voicingQ = (key, deg) => ({
  type: "build",
  key,
  deg,
  voicing: true,
  chord: chordFor(key, deg),
  label: `the shell of ${chordFor(key, deg)}`,
  targetNotes: shellVoicing(key, deg),
});

// An inversion question — play the full triad, but with a specific note in the
// BASS (inv 0 = root, 1 = 3rd, 2 = 5th). It's graded octave-aware (the lesson
// screen checks the lowest note), and `voiced` is the chord bottom-to-top so the
// "hear it" button can play the inversion. Slash-chord label, e.g. "C/E".
export const inversionQ = (key, deg, inv = 1) => {
  const e = entryFor(key, deg);
  const notes = (e && e.notes.slice(0, 3)) || []; // triad, root position
  const bass = notes[inv];
  const chord = chordFor(key, deg);
  const name = inv === 0 ? chord : `${chord}/${bass}`;
  return {
    type: "inversion",
    key,
    deg,
    inv,
    chord,
    name,
    label: name,
    notes, // the three pitch classes that must sound
    bass, // the required lowest note
    voiced: [...notes.slice(inv), ...notes.slice(0, inv)], // bottom-to-top
  };
};

const progressionQ = (key, degrees) => {
  const correct = progChords(key, degrees);
  const distract = progDistractors(correct, (p) => progChords(key, p), progPoolFor(key));
  return {
    type: "progression",
    key,
    degrees,
    prompt: `In ${key}:  ${degStr(degrees)}`,
    correct,
    options: shuffle([correct, ...distract]),
  };
};

/* ---------------- ear training v2 (#86) ----------------
   Ear answers are their own mastery dimension: a slot graded right writes to
   `key:deg:ear` (see earConceptId), so a weak ear resurfaces in review and
   shows in stats separately from knowing the chord on paper. */

// The tonic degree of a key — where an ear question's "move" starts from.
const homeDeg = (key) => (DIATONIC_MINOR[key] ? "i" : "I");

// The degree row an ear question answers from — the key's whole vocabulary,
// so the answer is recalled, not eliminated. Seventh-chord loops (the "add
// the 7th" unit) answer from the seventh row.
const earPoolFor = (key, degrees = []) =>
  degrees.some(isSeventh)
    ? [...SEVENTH_DEGREES]
    : DIATONIC_MINOR[key]
    ? [...MINOR_DEGREES]
    : [...DEGREES];

// The degrees of a loop worth asking as single-change moves — everything but
// home (the seventh loops' Imaj7 counts as home too).
const earMoveDegrees = (key, degrees) =>
  [...new Set(degrees)].filter((d) => d !== homeDeg(key) && d !== "Imaj7");

// Hear a loop, then name it — slot by slot. The learner fills each bar from
// the key's whole degree row rather than picking one of four whole-loop
// strings, so this trains hearing individual changes instead of pattern-
// matching the pool. Graded with partial credit per slot (earSlotGrade).
const earQ = (key, degrees) => ({
  type: "ear",
  key,
  degrees,
  pool: earPoolFor(key, degrees),
  correct: degStr(degrees),
});

// The single-change atom: hear home move to one chord, name where it went.
// (For the tonic itself the move is the resolution *back* home, V→I.)
export const earMoveQ = (key, deg) => {
  const seventh = isSeventh(deg);
  const minor = Boolean(DIATONIC_MINOR[key]);
  const home = seventh ? "Imaj7" : minor ? "i" : "I";
  const back = seventh ? "V7" : minor ? "v" : "V";
  const degrees = deg === home ? [back, home] : [home, deg];
  return {
    type: "earmove",
    key,
    deg,
    degrees,
    correct: deg,
    options: shuffle(earPoolFor(key, [deg])),
  };
};

// Hear most of a loop, stop, and call the next chord before it arrives —
// keyboardPlan Phase 5.2 ("predict the next chord") as a drill. `heard` is
// what plays before the question; the answer resolves audibly afterwards.
export const predictQ = (key, degrees) => {
  const deg = degrees[degrees.length - 1];
  return {
    type: "predict",
    key,
    deg,
    degrees,
    heard: degrees.slice(0, -1),
    correct: deg,
    options: shuffle(earPoolFor(key, degrees)),
  };
};

// A loop ear answer passes when at least this share of slots is right —
// partial credit per slot still feeds mastery either way.
export const EAR_PASS_PCT = 0.75;

// Grade a slot-by-slot ear answer: per-slot verdicts plus the overall pass.
export const earSlotGrade = (degrees, picks = []) => {
  const slots = degrees.map((deg, i) => ({
    deg,
    pick: picks[i] != null ? picks[i] : null,
    correct: picks[i] === deg,
  }));
  const right = slots.filter((s) => s.correct).length;
  return {
    slots,
    right,
    total: degrees.length,
    passed: right >= Math.ceil(degrees.length * EAR_PASS_PCT),
  };
};

// Map graded ear slots to mastery results under the ear dimension. No `ms` —
// listening time isn't the fluency being trained, so a right answer promotes.
export const earSlotResults = (key, slots) =>
  slots.map((s) => ({
    concept: earConceptId(key, s.deg),
    correct: s.correct,
    type: "ear",
  }));

// Title distractors lead with songs that share the answer's progression, so
// recognising the loop alone can't answer "which song is this?" — the learner
// has to place *this* recording, which is the real-world version of the skill.
const songTitleDistractors = (song) => {
  const others = STARTER_SONGS.filter((s) => s.title !== song.title);
  const same = others.filter((s) => degStr(s.numbers) === degStr(song.numbers));
  const rest = others.filter((s) => degStr(s.numbers) !== degStr(song.numbers));
  return [...shuffle(same), ...shuffle(rest)].slice(0, 3).map((s) => s.title);
};

const songQ = (song, ask) => {
  if (ask === "title") {
    const distract = songTitleDistractors(song);
    return {
      type: "song",
      ask: "title",
      songTitle: song.title,
      artist: song.artist,
      key: song.key,
      degrees: song.numbers,
      correct: song.title,
      options: shuffle([song.title, ...distract]),
    };
  }
  const correct = degStr(song.numbers);
  // minor-key songs draw minor-loop distractors, majors the common loops
  const distract = progDistractors(correct, (p) => degStr(p), progPoolFor(song.key));
  return {
    type: "song",
    ask: "numbers",
    songTitle: song.title,
    artist: song.artist,
    key: song.key,
    degrees: song.numbers,
    correct,
    options: shuffle([correct, ...distract]),
  };
};

/* ---------------- find the tonic (#24) ----------------
   Cadences that resolve firmly home, so the ear can locate "1". */
const TONIC_CADENCES = [
  ["IV", "V", "I"],
  ["ii", "V", "I"],
  ["V", "I"],
  ["I", "IV", "V", "I"],
];

// "Find the tonic": play a cadence that lands home, then ask which note is home.
// Options are scale-tone names (the answer is a note, not a number), and the key
// is deliberately not revealed — the whole point is to hear "home".
const tonicQ = (key) => {
  const scale = scaleFor(key) || [];
  const tonic = scale[0];
  const distract = sample(scale.slice(1), 3, []);
  return {
    type: "tonic",
    key,
    degrees: pick(TONIC_CADENCES),
    correct: tonic,
    options: shuffle([tonic, ...distract]),
  };
};

/* ---------------- cadence / function (#26) ----------------
   The four moves a learner should be able to tell apart by ear: where a short
   move "lands" (tonic I / vi = home, IV / ii = away, V = tension). */
export const CADENCES = [
  { id: "authentic", degrees: ["V", "I"], label: "authentic (V→I)" },
  { id: "plagal", degrees: ["IV", "I"], label: "plagal (IV→I)" },
  { id: "half", degrees: ["I", "V"], label: "half (→V)" },
  { id: "deceptive", degrees: ["V", "vi"], label: "deceptive (V→vi)" },
];

// "Name the cadence": play a two-chord move, ask which cadence it is.
const cadenceQ = (key) => {
  const c = pick(CADENCES);
  const distract = sample(
    CADENCES.map((x) => x.label),
    3,
    [c.label]
  );
  return {
    type: "cadence",
    key,
    degrees: c.degrees,
    cadenceId: c.id,
    correct: c.label,
    options: shuffle([c.label, ...distract]),
  };
};

/* ---------------- complete the progression ----------------
   A loop with one slot blanked — the learner fills the gap in context, which is
   what number-thinking is actually for. Two directions: blank a chord (read the
   numbers, supply the chord) or blank a number (read the chords, supply the
   number). The full loop is playable so the ear helps too. */
// Like chord2numQ, the options are the key's full degree / chord row (seven),
// so the blank has to be recalled, not eliminated.
const completeQ = (key, degrees, dir = pick(["chord", "num"])) => {
  const blank = randInt(0, degrees.length - 1);
  const deg = degrees[blank];
  if (dir === "num") {
    const pool = DIATONIC_MINOR[key]
      ? MINOR_DEGREES
      : isSeventh(deg)
      ? SEVENTH_DEGREES
      : DEGREES;
    return {
      type: "complete",
      dir: "num",
      key,
      degrees,
      blank,
      deg,
      correct: deg,
      options: shuffle([...pool]),
    };
  }
  const correct = chordFor(key, deg);
  return {
    type: "complete",
    dir: "chord",
    key,
    degrees,
    blank,
    deg,
    correct,
    options: shuffle([...new Set(tableFor(key, deg).map((c) => c.chord))]),
  };
};

/* ---------------- play the progression (MIDI capstone) ----------------
   Play a whole loop, chord by chord, on the keyboard — the real comping skill.
   Each step carries the chord's pitch-class set so the lesson screen can grade a
   played chord octave-agnostically, exactly like a build question. Surfaced only
   when a MIDI keyboard is connected (tedious to tap a 4-chord loop on glass). */
const playProgQ = (key, degrees) => ({
  type: "playprog",
  key,
  degrees,
  steps: degrees.map((d) => (entryFor(key, d) || { notes: [] }).notes),
});

/* ---------------- timed play-the-changes ----------------
   The same play-the-loop skill as playProgQ, but performed against the rhythm
   section: the groove plays, one chord per bar, and each chord has to land
   near its downbeat. This is the band drill — the song doesn't wait — and it
   works on the glass piano or the typing keyboard, so audio support (not
   MIDI) is the only gate. The lesson screen owns the transport; the grading
   here is pure so it's unit-testable. */
export const TIMED_PASSES = 2; // times through the loop per run
export const ON_TIME_BEATS = 1.5; // land within this many beats of the downbeat
export const TIMED_PASS_PCT = 0.6; // fraction of bars on time to pass the question

export const timedProgQ = (key, degrees) => ({
  type: "playprog",
  timed: true,
  key,
  degrees,
  steps: degrees.map((d) => (entryFor(key, d) || { notes: [] }).notes),
});

// Grade one bar of a timed run. `strikes` are { name, t } note-ons (audio-
// clock seconds, any input: MIDI, glass, typing); the bar starts at `barStart`
// and a beat lasts `beatSec`. The bar is hit once every target pitch-class has
// been struck (extra notes are forgiven — real playing has smudges) and is on
// time when the last of them lands within ON_TIME_BEATS of the downbeat.
export const timedBarResult = (strikes = [], target = [], barStart = 0, beatSec = 0.6) => {
  const need = new Set(target);
  if (!need.size) return { hit: false, onTime: false, latency: null };
  const got = new Set();
  let landed = null;
  [...strikes]
    .sort((a, b) => a.t - b.t)
    .some((s) => {
      if (!need.has(s.name)) return false;
      got.add(s.name);
      if (got.size === need.size) {
        landed = s.t;
        return true;
      }
      return false;
    });
  if (landed == null) return { hit: false, onTime: false, latency: null };
  const latency = landed - barStart;
  return { hit: true, onTime: latency <= beatSec * ON_TIME_BEATS, latency };
};

// Roll per-bar results into the run verdict: bars hit / on time, the average
// landing latency, and whether the run passes (enough bars on the beat).
export const timedRunSummary = (bars = []) => {
  const total = bars.length;
  const hits = bars.filter((b) => b.hit).length;
  const onTime = bars.filter((b) => b.onTime).length;
  const lats = bars.filter((b) => b.latency != null).map((b) => b.latency);
  return {
    total,
    hits,
    onTime,
    avgLatencyMs: lats.length
      ? Math.round((lats.reduce((a, b) => a + b, 0) / lats.length) * 1000)
      : null,
    passed: total > 0 && onTime >= Math.ceil(total * TIMED_PASS_PCT),
  };
};

/* ---------------- comp the progression (voicing track) ----------------
   The same play-it-in-time runtime as playProgQ, but each step's target is the
   chord's *shell* (guide tones), not the full chord — so the player comps the
   loop with rootless voicings. `voicing` flags it for the lesson screen. Unlike
   playProgQ this is surfaced with or without a keyboard: comping a loop is the
   whole point of the voicing track, so it's worth tapping out on glass too. */
const compProgQ = (key, degrees) => ({
  type: "playprog",
  voicing: true,
  key,
  degrees,
  steps: degrees.map((d) => shellVoicing(key, d)),
});

/* ---------------- comp the changes in time (#95) ----------------
   Where rhythm and changes finally meet: the groove plays, and the player has
   to hold a comping *pattern* through the loop — right shell notes AND hits
   on the pattern's 16th grid, graded per bar. The pattern trainer teaches the
   feel on one chord; this is the same feel under moving harmony, which is the
   actual gig skill. Uses the timed transport in the lesson screen; grading is
   pure so it's unit-testable. */

// Patterns simple enough to grade a lesson on (eighths is a workout, arp/broken
// walk single tones — both stay in the trainer/jam room, out of the exams).
const TIMED_COMP_PATTERNS = ["pads", "push", "stabs", "half"];

// A bar counts as "on the pattern" at this rhythm-match score (see scoreRhythm
// — forgiving by design; the notes axis is graded separately).
export const COMP_TIGHT_PCT = 60;

export const timedCompQ = (key, degrees, pattern = pick(TIMED_COMP_PATTERNS)) => ({
  type: "playprog",
  timed: true,
  voicing: true,
  pattern,
  key,
  degrees,
  steps: degrees.map((d) => shellVoicing(key, d)),
});

// Grade one bar of a timed comp run on both axes. `strikes` are { name, t }
// note-ons; the bar is *hit* when every shell tone sounded somewhere in the
// bar, and *onTime* when it's hit and the strike rhythm matches the pattern's
// grid closely enough. `score` is the 0–100 rhythm match either way.
export const timedCompBarResult = (
  strikes = [],
  target = [],
  patternHits = [],
  barStart = 0,
  beatSec = 0.6
) => {
  const need = new Set(target);
  const struck = new Set(strikes.map((s) => s.name));
  const hit = need.size > 0 && [...need].every((n) => struck.has(n));
  const grouped = groupStrikes(strikes.map((s) => s.t));
  const { score } = scoreRhythm(grouped, barStart, beatSec / 4, patternHits, 1);
  return { hit, onTime: hit && score >= COMP_TIGHT_PCT, score, latency: null };
};

/* ---------------- grade the rests (#98) ----------------
   Restraint is a graded skill: a loop's bar can be a REST — the chart says lay
   out, the drummer fills, and the one wrong answer is playing. Grading counts
   note-*ons* from the bar's downbeat onward, so a chord struck late in the
   previous bar (or held ringing through the rest) doesn't count against it —
   what's measured is whether the hands re-attacked, not whether sound decayed. */

// Grade a rest bar: silence is a hit (and "on time" — you can't be late at
// laying out); any strike inside the bar is a miss. This is the one place the
// timed graders do NOT forgive extra notes — that's the whole lesson.
export const timedRestBarResult = (strikes = [], barStart = 0) => {
  const played = strikes.some((s) => s.t >= barStart);
  return { rest: true, hit: !played, onTime: !played, score: null, latency: null };
};

// A loop with its last bar handed to the drummer — the classic "lay out for
// the fill" shape (comp 3, rest 1).
export const withRestBar = (degrees = []) =>
  degrees.length ? [...degrees.slice(0, -1), REST] : degrees;

// The "leave room" drill: a timed comp run whose last bar is a rest. Shells on
// a pattern for three bars, hands off for the fill — `space` flags it so the
// screen can spell out that silence is the answer. Rest steps target no notes
// (shellVoicing(REST) is []), and the rest bars are graded by
// timedRestBarResult instead of the pattern grader.
export const leaveRoomQ = (key, degrees, pattern) => ({
  ...timedCompQ(key, withRestBar(degrees), pattern),
  space: true,
});

// The comp run's verdict: the usual timed roll-up plus the average rhythm
// match, since "how tight was the feel" is the number this question is about.
export const timedCompRunSummary = (bars = []) => {
  const base = timedRunSummary(bars);
  const scores = bars.filter((b) => b.score != null).map((b) => b.score);
  return {
    ...base,
    avgScore: scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : null,
  };
};

/* ---------------- voice leading (voicing track, v2 — #96) ----------------
   Voice leading is a *choice*, not an order: from wherever your hand sits, any
   inversion of the next chord is a correct answer — what's measured is how far
   the hand travels. The metric is octave-aware (a voicing is realized as
   ascending pitches, and travel is the summed semitone motion voice-by-voice,
   with the whole voicing's register free), which is what makes inversions
   genuinely differ — a pitch-class-only distance can't tell them apart. */

// Realize a bottom-to-top pitch-class voicing as ascending semitone values
// (each note the nearest step above the one below it).
export const realizeVoicing = (names = []) => {
  const out = [];
  names.forEach((n) => {
    let v = SEMITONES[n];
    if (v == null) return;
    while (out.length && v <= out[out.length - 1]) v += 12;
    out.push(v);
  });
  return out;
};

// The semitone travel from one voicing to another (both bottom-to-top pitch-
// class lists, same size). Voices pair off in order; the destination may sit
// in whichever octave is closest, so register doesn't count — only motion.
export const voiceTravel = (fromNames = [], toNames = []) => {
  const a = realizeVoicing(fromNames);
  const b = realizeVoicing(toNames);
  if (!a.length || a.length !== b.length) return 0;
  let best = Infinity;
  [-12, 0, 12].forEach((shift) => {
    const cost = b.reduce((s, v, i) => s + Math.abs(v + shift - a[i]), 0);
    if (cost < best) best = cost;
  });
  return best;
};

// The travel of an actually-played voicing (MIDI note numbers) from a
// reference voicing. Doubled octaves collapse to the lowest key per pitch
// class; the played register is normalized away like voiceTravel does.
// Returns null when the shapes can't be compared (different voice counts).
export const playedTravel = (fromNames = [], midis = []) => {
  const a = realizeVoicing(fromNames);
  const byPc = {};
  [...midis]
    .sort((x, y) => x - y)
    .forEach((m) => {
      const pc = m % 12;
      if (byPc[pc] == null) byPc[pc] = m;
    });
  const b = Object.values(byPc).sort((x, y) => x - y);
  if (!a.length || a.length !== b.length) return null;
  const align = Math.round((b[0] - a[0]) / 12) * 12;
  let best = Infinity;
  [align - 12, align, align + 12].forEach((shift) => {
    const cost = b.reduce((s, v, i) => s + Math.abs(v - shift - a[i]), 0);
    if (cost < best) best = cost;
  });
  return best;
};

// The rotation (inversion) of `notes` that travels least from `prevNames`,
// with its cost — the optimum a voice-lead answer is measured against.
export const smoothestVoicing = (prevNames, notes = []) => {
  if (!prevNames || !prevNames.length || !notes.length) {
    return { voiced: [...notes], cost: 0, inv: 0 };
  }
  let best = null;
  for (let i = 0; i < notes.length; i += 1) {
    const voiced = [...notes.slice(i), ...notes.slice(0, i)];
    const cost = voiceTravel(prevNames, voiced);
    if (!best || cost < best.cost) best = { voiced, cost, inv: i };
  }
  return best;
};

// Semitones of travel past the optimum that still count as smooth — right
// chord + short path promotes mastery; right-but-wide holds the box.
export const VOICELEAD_SLACK = 2;

// A voice-lead step: play the named chord in ANY inversion — grading checks
// the pitch-class set, then scores the hand's travel from `prev` against
// `bestCost` (see playedTravel / LessonScreen). `best` is the smoothest
// voicing, used by the "hear the move" button. Shells voice-lead the same
// way with their two guide tones.
export const voiceLeadQ = (key, deg, prevNotes, opts = {}) => {
  const shell = Boolean(opts.shell);
  const e = entryFor(key, deg);
  const notes = shell ? shellVoicing(key, deg) : (e && e.notes.slice(0, 3)) || [];
  const { voiced, cost } = smoothestVoicing(prevNotes, notes);
  const chord = chordFor(key, deg);
  return {
    type: "voicelead",
    key,
    deg,
    shell,
    chord,
    label: shell ? `the shell of ${chord}` : chord,
    notes, // the pitch classes that must sound (any inversion)
    prev: [...prevNotes], // where the hand is coming from (bottom-to-top)
    prevLabel: opts.prevLabel || null,
    best: voiced, // the smoothest destination (for "hear the move")
    bestCost: cost,
  };
};

// The chord tones two changes share — the note(s) a smooth hand holds. Shells
// compare guide tones for seventh degrees, triads otherwise.
const leadTones = (key, deg) =>
  isSeventh(deg)
    ? shellVoicing(key, deg)
    : (entryFor(key, deg) || { notes: [] }).notes.slice(0, 3);
export const commonTones = (key, degA, degB) =>
  leadTones(key, degA).filter((n) => leadTones(key, degB).includes(n));

// "Which note carries over?" — the awareness half of voice leading. Any shared
// tone is accepted (`accept`); options are the key's whole scale row.
export const commonToneQ = (key, degA, degB) => {
  const shared = commonTones(key, degA, degB);
  return {
    type: "common",
    key,
    degrees: [degA, degB],
    chords: [chordFor(key, degA), chordFor(key, degB)],
    prompt: `${chordFor(key, degA)} → ${chordFor(key, degB)}: which note carries over?`,
    correct: shared[0],
    accept: shared,
    options: shuffle([...(scaleFor(key) || [])]),
  };
};

// A voice-led loop: anchor the hand in root position on the first chord, then
// every later change is a free-inversion voice-lead step. The assumed "where
// you're coming from" chain follows the smoothest path, so each step's
// optimum is honest.
export const voiceLeadSequence = (key, degrees) => {
  const qs = [];
  let prev = null;
  let prevLabel = null;
  degrees.forEach((deg) => {
    const e = entryFor(key, deg);
    const triad = (e && e.notes.slice(0, 3)) || [];
    if (!triad.length) return;
    if (!prev) qs.push(inversionQ(key, deg, 0)); // anchor: root position first
    else qs.push(voiceLeadQ(key, deg, prev, { prevLabel }));
    prev = smoothestVoicing(prev, triad).voiced;
    prevLabel = chordFor(key, deg);
  });
  return qs;
};

// The shells version — ii–V–I is where the magic shows: one guide tone holds
// while the 3rd of one chord *becomes* the 7th of the next. First shell is a
// free build (voicingQ), then each change is a two-note voice-lead step.
export const shellLeadSequence = (key, degrees) => {
  const qs = [];
  let prev = null;
  let prevLabel = null;
  degrees.forEach((deg) => {
    const shell = shellVoicing(key, deg);
    if (!shell.length) return;
    if (!prev) qs.push(voicingQ(key, deg));
    else qs.push(voiceLeadQ(key, deg, prev, { shell: true, prevLabel }));
    prev = smoothestVoicing(prev, shell).voiced;
    prevLabel = chordFor(key, deg);
  });
  return qs;
};

/* ---------------- color chords & bass motion (voicing track, #97) ----------
   The pop/worship texture toolkit: sus chords (swap the 3rd out, then resolve
   it back), the add9 shimmer, and slash-chord walk-ups — inversions doing
   their real job, which is bass motion. Everything derives from the key's
   scale, so it stays diatonic and works in every key the app teaches.
   (F's diatonic 4th is the lydian #4, so sus4 lives on I and V only.) */

export const SUS4_DEGREES = ["I", "V"];
export const SUS2_DEGREES = ["I", "IV", "V"];
export const ADD9_DEGREES = ["I", "IV"];

// The color variant of a degree's chord: sus4 (root–4th–5th), sus2
// (root–2nd–5th) or add9 (triad + the 2nd), spelled from the key's scale.
export const colorChord = (key, deg, kind) => {
  const scale = scaleFor(key) || [];
  const n = DEGREES.indexOf(deg);
  if (n < 0 || scale.length < 7) return null;
  const at = (o) => scale[(n + o) % 7];
  const root = at(0);
  if (kind === "sus4") return { chord: `${root}sus4`, notes: [root, at(3), at(4)] };
  if (kind === "sus2") return { chord: `${root}sus2`, notes: [root, at(1), at(4)] };
  return { chord: `${root}add9`, notes: [root, at(1), at(2), at(4)] };
};

// Build the color chord on the keys — a build question with its own recipe
// hint (see buildHint). Not a shell: `color` is its own kind of target.
export const colorQ = (key, deg, kind) => {
  const c = colorChord(key, deg, kind);
  return {
    type: "build",
    color: kind,
    key,
    deg,
    chord: c.chord,
    label: c.chord,
    targetNotes: c.notes,
  };
};

// Tension, then release: play the color chord, then resolve it to the plain
// triad — a two-step playprog with explicit labels (Gsus4 → G).
export const resolveQ = (key, deg, kind = "sus4") => {
  const c = colorChord(key, deg, kind);
  const e = entryFor(key, deg);
  return {
    type: "playprog",
    resolve: true,
    key,
    deg,
    degrees: [deg, deg],
    labels: [c.chord, e.chord],
    steps: [c.notes, e.notes.slice(0, 3)],
  };
};

// The money move in context: comp I → Vsus4 → V → IV, the suspension held
// for a bar and released — full chords, labels carrying the color.
export const colorCompQ = (key) => {
  const tri = (d) => (entryFor(key, d) || { notes: [] }).notes.slice(0, 3);
  const sus = colorChord(key, "V", "sus4");
  return {
    type: "playprog",
    color: true,
    key,
    degrees: ["I", "V", "V", "IV"],
    labels: [chordFor(key, "I"), sus.chord, chordFor(key, "V"), chordFor(key, "IV")],
    steps: [tri("I"), sus.notes, tri("V"), tri("IV")],
  };
};

// Slash chords doing their job: walk the bass up by scale steps (I → I/3 →
// IV) and down (I → V/3 → vi). Each step is an inversion question, graded
// lowest-note-aware — the walk is the point, the slides explain it.
export const walkUpSequence = (key) => [
  inversionQ(key, "I", 0),
  inversionQ(key, "I", 1), // I/3 — the bass steps up to the 3rd
  inversionQ(key, "IV", 0),
];
export const walkDownSequence = (key) => [
  inversionQ(key, "I", 0),
  inversionQ(key, "V", 1), // V/3 — the passing chord of every ballad walk-down
  inversionQ(key, "vi", 0),
];

/* ---------------- capstones ("put it together") ----------------
   The synthesis layer lives in its own lesson nodes, not inside a unit's
   drilling lessons: a per-unit capstone after each standard unit, and a track-
   level final exam after the last unit (see trackPath). Both lean on musical,
   mixed-mode questions — including the cadence / find-the-tonic ear checks the
   ordinary lessons never use (those only lived in the practice games until now).
   Every question is flagged `{ capstone: true }` so the lesson screen marks it.

   Every plan unit gets a capstone — the per-key units, the sevenths unit, and
   the voicing units (which get a comping-flavoured one, see buildVoicingCapstone).
   Cadence labels (V→I, IV→I …) and the find-the-tonic resolutions are major-
   diatonic, so the function-ear check is a major-track flourish — minor
   capstones skip it rather than ask for numerals a minor key doesn't have. */
export const isStandardUnit = (u) => Boolean(u.plan);

// The voicing track's capstone is about *playing*, not ear-naming: build a
// couple of shells / inversions, then comp (or voice-lead) the whole loop.
function buildVoicingCapstone(unit, opts = {}) {
  const key = pick(KEYS);
  const degs = unit.degrees || [];
  const loop = unit.prog || [];
  const qs = [];
  if (unit.color) {
    // color & motion: a sus build, its release, the walk-up chord, then comp
    // the unit's loop (in time when audio can drive the groove)
    qs.push(colorQ(key, pick(SUS4_DEGREES), pick(["sus4", "sus2"])));
    qs.push(resolveQ(key, "V", "sus4"));
    qs.push(inversionQ(key, "I", 1)); // I/3 — the walk-up passing chord
    qs.push(opts.timed ? timedCompQ(key, loop) : compProgQ(key, loop));
    return qs.map((q) => ({ ...q, capstone: true }));
  }
  if (unit.space) {
    // leave room: a shell build, then the leave-room run itself — comp the
    // loop and lay out for the fill (plain shell comping without audio)
    qs.push(voicingQ(key, pick(degs)));
    qs.push(opts.timed ? leaveRoomQ(key, loop) : compProgQ(key, loop));
    return qs.map((q) => ({ ...q, capstone: true }));
  }
  if (unit.inversions) {
    sample(degs, Math.min(2, degs.length)).forEach((deg) =>
      qs.push(inversionQ(key, deg, pick([1, 2])))
    );
    qs.push(...voiceLeadSequence(key, loop)); // voice-lead the whole loop
  } else {
    sample(degs, Math.min(2, degs.length)).forEach((deg) =>
      qs.push(voicingQ(key, deg))
    );
    // comp the whole loop with shells — in time, on a pattern, when audio
    // can drive the groove; free-time otherwise
    qs.push(opts.timed ? timedCompQ(key, loop) : compProgQ(key, loop));
  }
  return qs.map((q) => ({ ...q, capstone: true }));
}

// A unit's "put it together" capstone: recall the loop in context, spell it,
// name one by ear, a function-ear check (major), the anchored song, and — with
// a keyboard — comp the whole loop. Voicing units use a playing-focused one.
export function buildCapstone(unit, opts = {}) {
  if (unit.voicing) return buildVoicingCapstone(unit, opts);
  const midi = Boolean(opts.midi);
  const key = unit.key;
  const pool = unit.minor ? MINOR_PROGS : COMMON_PROGS;
  const loop = unit.prog || pick(pool);
  const modes = unit.modes || [];
  const qs = [];
  qs.push(completeQ(key, loop)); // recall a chord/number in the loop's context
  qs.push(progressionQ(key, loop)); // spell the whole signature loop
  // production check: build one of the loop's chords by hand — a capstone
  // can't be passed on recognition alone, keyboard or not
  const buildable = [...new Set(loop)].filter((d) => entryFor(key, d));
  if (buildable.length) qs.push(buildQ(key, pick(buildable)));
  // the ear check: one single-change atom, then name a (fresh) loop by ear
  const moveDegs = earMoveDegrees(key, loop);
  if (moveDegs.length) qs.push(earMoveQ(key, pick(moveDegs)));
  qs.push(earQ(key, pick(pool)));
  if (!unit.minor) qs.push(Math.random() < 0.5 ? cadenceQ(key) : tonicQ(key));
  // close on the unit's anchored song, or (older units) any song they emphasise
  const wantsSong = unit.song || modes.includes("song");
  if (wantsSong) {
    const s = unit.song ? songByTitle(unit.song) : pick(songPoolFor(unit));
    if (s) qs.push(songQ(s, "numbers"));
  }
  if (opts.timed) qs.push(timedProgQ(key, loop));
  else if (midi) qs.push(playProgQ(key, loop));
  return qs.map((q) => ({ ...q, capstone: true }));
}

// The track's final exam: a longer mixed set sampled across the whole track's
// standard units, so it spans every key the track taught. Recall-in-context and
// spelling across a few units, ear loops in two keys, a function check and a
// song (major tracks — the starter songs are all major), and a play-the-loop
// finale with a keyboard. Returns [] if the track has no standard units.
export function buildTrackCapstone(track, opts = {}) {
  const midi = Boolean(opts.midi);
  const units = (track.units || []).filter(isStandardUnit);
  if (!units.length) return [];
  // the voicing track's final exam is a comp-everything finale across keys —
  // two *distinct* keys, so the exam genuinely spans the track's range
  if (units[0].voicing) {
    const [k1, k2] = sample(KEYS, 2);
    const qs = [
      compProgQ(k1, ["I", "V", "vi", "IV"]),
      compProgQ(k2, ["ii7", "V7", "Imaj7"]),
      ...voiceLeadSequence(pick(KEYS), ["I", "vi", "IV", "V"]),
    ];
    // the true finale: hold a comping pattern through the changes, in time —
    // and lay out for the fill, because that's what a bandleader hires for
    if (opts.timed) qs.push(leaveRoomQ(pick(KEYS), ["I", "V", "vi", "IV"]));
    return qs.map((q) => ({ ...q, capstone: true }));
  }
  const minor = Boolean(units[0].minor);
  const pool = minor ? MINOR_PROGS : COMMON_PROGS;
  const loopFor = (u) => u.prog || pick(pool);
  const qs = [];
  // recall + spelling across a few distinct units (covers the range of keys)
  shuffle(units)
    .slice(0, Math.min(4, units.length))
    .forEach((u, i) =>
      qs.push(i % 2 === 0 ? completeQ(u.key, loopFor(u)) : progressionQ(u.key, loopFor(u)))
    );
  // name two loops by ear, in (up to) two different keys, warmed up by one
  // single-change atom in the first of them
  shuffle(units)
    .slice(0, 2)
    .forEach((u, i) => {
      if (i === 0) {
        const md = earMoveDegrees(u.key, loopFor(u));
        if (md.length) qs.push(earMoveQ(u.key, pick(md)));
      }
      qs.push(earQ(u.key, loopFor(u)));
    });
  // production check: build a chord from one unit's loop by hand
  const bu = pick(units);
  const bloop = loopFor(bu);
  const buildable = [...new Set(bloop)].filter((d) => entryFor(bu.key, d));
  if (buildable.length) qs.push(buildQ(bu.key, pick(buildable)));
  // a function-ear check and a real song — major tracks only
  if (!minor) {
    const u = pick(units);
    qs.push(Math.random() < 0.5 ? cadenceQ(u.key) : tonicQ(u.key));
    qs.push(songQ(pick(MAJOR_SONGS), Math.random() < 0.5 ? "numbers" : "title"));
  }
  // play a whole loop as the finale — in time when audio can drive a groove
  if (opts.timed) {
    const u = pick(units);
    qs.push(timedProgQ(u.key, loopFor(u)));
  } else if (midi) {
    const u = pick(units);
    qs.push(playProgQ(u.key, loopFor(u)));
  }
  return qs.map((q) => ({ ...q, capstone: true }));
}

/* The ordered node path for a track: each unit's lessons, then that unit's
   "put it together" capstone (standard units only), and finally a track-level
   final exam. It's a strict linear chain, so a node unlocks once the node
   before it is complete — which makes the capstones *required* to move on.
   Capstone node ids (`${unitId}-cap`, `${trackId}-final`) never match the
   `${unitId}-${lessonIdx}` keys unlockedConcepts / the game gates read, so a
   capstone introduces no new concepts; it's purely a checkpoint. */
// A short label for a plan lesson, surfaced on the path node ("scale", "major",
// "minor", "progressions"…) so the per-key structure reads at a glance.
const planLessonLabel = (spec) => {
  if (!spec) return null;
  if (spec.kind === "scale") return "scale";
  if (spec.kind === "prog") return "progressions";
  if (spec.kind === "voicing") return spec.seventh ? "shell 7ths" : "shells";
  if (spec.kind === "comp") return "comp it";
  if (spec.kind === "inversion") return "inversions";
  if (spec.kind === "voicelead") return spec.shell ? "lead the shells" : "voice lead";
  if (spec.kind === "color") return "color chords";
  if (spec.kind === "walk") return "walk the bass";
  return spec.tag || (spec.quality === "major" ? "major" : "minor");
};

export function trackPath(track) {
  const nodes = [];
  (track.units || []).forEach((u) => {
    for (let i = 0; i < lessonCount(u); i += 1) {
      nodes.push({
        id: `${u.id}-${i}`,
        kind: "lesson",
        unitId: u.id,
        idx: i,
        lessonLabel: u.plan ? planLessonLabel(u.plan[i]) : null,
      });
    }
    if (isStandardUnit(u)) {
      nodes.push({ id: `${u.id}-cap`, kind: "unitcap", unitId: u.id });
    }
  });
  if ((track.units || []).some(isStandardUnit)) {
    nodes.push({ id: `${track.id}-final`, kind: "trackcap", trackId: track.id });
  }
  return nodes;
}

/* ---------------- scale-recall question ----------------
   For the scale lesson: name a scale note by its number, or a number by its
   note. A plain choice question over the key's seven scale tones. */
const scaleNoteQ = (key, dir = pick(["num2note", "note2num"]), idx = null) => {
  const scale = scaleFor(key) || [];
  const i = idx != null ? idx : randInt(0, scale.length - 1);
  const note = scale[i];
  const num = String(i + 1);
  // Options are the whole scale row (all seven numbers / notes) — recall the
  // answer, don't eliminate it.
  if (dir === "note2num") {
    return {
      type: "scalenote",
      dir,
      key,
      prompt: `In ${key}, what number is ${note}?`,
      correct: num,
      options: scale.map((_, j) => String(j + 1)),
    };
  }
  return {
    type: "scalenote",
    dir,
    key,
    prompt: `In ${key}, what's note ${i + 1} of the scale?`,
    correct: note,
    options: [...scale],
  };
};

// Once a concept reaches this Leitner box, recognition questions retire and
// the concept is drilled by *production* (build / play it) even on glass —
// choice buttons are training wheels, not the skill.
export const PRODUCTION_STRENGTH = 2;

// Has this (key, degree) earned production-only drilling?
const isStrong = (mastery, key, deg) =>
  conceptStrength(mastery, `${key}:${deg}`) >= PRODUCTION_STRENGTH;

// One recognition question for a chord — number→chord or chord→number at
// random. A keyboard, or a production-strength concept, turns recognition
// into playing it.
const recallQ = (key, deg, opts = {}) =>
  opts.midi || isStrong(opts.mastery, key, deg)
    ? buildQ(key, deg)
    : Math.random() < 0.5
    ? num2chordQ(key, deg)
    : chord2numQ(key, deg);

/* ---------------- plan-unit lesson builder ----------------
   Build one lesson of a per-key unit, dispatching on the lesson's `kind`:
   - scale : build the whole scale, then quiz the note↔number mapping
   - chords: recognise each chord (random direction) AND build it by hand
   - prog  : read / fill the unit's loops, then name some by ear
   Order is shuffled and counts/directions/loops are drawn at random so re-runs
   vary. With a MIDI keyboard the chord work becomes playing, and a prog lesson
   ends on playing the whole loop. */
function buildPlanLesson(unit, spec, opts = {}) {
  const midi = Boolean(opts.midi);
  const key = unit.key;
  if (!spec) return [];

  if (spec.kind === "scale") {
    // build the whole scale, then quiz the note↔number mapping a handful of
    // times (random notes + directions, shuffled) for a fuller, varied lesson.
    const quiz = Array.from({ length: randInt(4, 6) }, () => scaleNoteQ(key));
    return [scaleQ(key), ...shuffle(quiz)];
  }

  if (spec.kind === "chords") {
    const degs = spec.degrees || [];
    const qs = [];
    // recognise each chord (random direction; playing once it's strong) …
    degs.forEach((deg) => qs.push(recallQ(key, deg, opts)));
    // … then build each one by hand. Where recognition already *is* playing
    // (MIDI, or a production-strength concept) don't repeat the same build.
    if (midi) {
      if (degs.length) qs.push(buildQ(key, pick(degs)));
    } else {
      degs
        .filter((deg) => !isStrong(opts.mastery, key, deg))
        .forEach((deg) => qs.push(buildQ(key, deg)));
    }
    return shuffle(qs);
  }

  if (spec.kind === "prog") {
    const all = spec.progs && spec.progs.length ? spec.progs : [unit.prog];
    const fresh = all[all.length - 1]; // the loop this key adds
    const prior = all.slice(0, -1); // earlier loops, carried forward to reinforce
    // the ear atoms come first: hear home move to each of the new loop's
    // chords one at a time, before any whole-loop listening
    const moves = sample(earMoveDegrees(key, fresh), 2).map((d) =>
      earMoveQ(key, d)
    );
    // focus on the new loop (spell it, fill it, hear it) …
    const onNew = [
      progressionQ(key, fresh),
      completeQ(key, fresh),
      earQ(key, fresh),
    ];
    // … reinforce a couple of earlier loops (carried forward), mixing fill + ear.
    // The first key has none yet, so it drills the new loop a bit more instead.
    const reinforce = prior.length
      ? sample(prior, Math.min(2, prior.length)).map((loop, i) =>
          i % 2 === 0 ? completeQ(key, loop) : earQ(key, loop)
        )
      : [completeQ(key, fresh), earQ(key, fresh)];
    // a musical closer — an earlier loop by ear, or the new one if none yet
    const closer = earQ(key, prior.length ? pick(prior) : fresh);
    // the finale is *playing* the new loop: in time against the groove when
    // audio can drive one, or free-time under bare MIDI
    const tail = opts.timed
      ? [timedProgQ(key, fresh)]
      : midi
      ? [playProgQ(key, fresh)]
      : [];
    return [...moves, ...shuffle([...onNew, ...reinforce]), closer, ...tail];
  }

  // ---- voicing-track lesson kinds ----
  // Voicing/comp/inversion/voice-lead drills roam C/G/D/A so the shapes become
  // portable (comping over keys), rather than living only in the unit's home key.
  if (spec.kind === "voicing") {
    const degs = spec.degrees || [];
    const keyAt = () => pick(KEYS);
    // build each chord's shell (root left out), in varied keys; an extra for length
    const qs = degs.map((deg) => voicingQ(keyAt(), deg));
    if (degs.length) qs.push(voicingQ(keyAt(), pick(degs)));
    return shuffle(qs);
  }
  if (spec.kind === "comp") {
    const key = pick(KEYS);
    const loop = spec.prog || unit.prog || [];
    // build each shell in the loop first, then comp the whole loop with shells
    // — against the groove, holding a pattern, when audio can drive one
    const qs = [...new Set(loop)].map((deg) => voicingQ(key, deg));
    qs.push(opts.timed ? timedCompQ(key, loop) : compProgQ(key, loop));
    return qs;
  }
  if (spec.kind === "inversion") {
    const key = pick(KEYS);
    const degs = spec.degrees || [];
    return shuffle(
      degs.flatMap((deg) => [inversionQ(key, deg, 1), inversionQ(key, deg, 2)])
    );
  }
  if (spec.kind === "voicelead") {
    const key = pick(KEYS);
    const loop = spec.prog || unit.prog || [];
    // warm up on awareness: name the tone adjacent changes share (the one a
    // smooth hand holds), then play the loop with free-inversion lead steps
    const pairs = loop
      .slice(1)
      .map((d, i) => [loop[i], d])
      .filter(([a, b]) => a !== b && commonTones(key, a, b).length > 0);
    const warm = sample(pairs, Math.min(2, pairs.length)).map(([a, b]) =>
      commonToneQ(key, a, b)
    );
    return [
      ...warm,
      ...(spec.shell ? shellLeadSequence(key, loop) : voiceLeadSequence(key, loop)),
    ];
  }
  if (spec.kind === "color") {
    // build each color, and resolve every suspension you build — tension is
    // only a color once the release is in the hands. Ends on the money move.
    const key = pick(KEYS);
    return [
      colorQ(key, "I", "sus4"),
      resolveQ(key, "I", "sus4"),
      colorQ(key, pick(SUS2_DEGREES), "sus2"),
      colorQ(key, pick(ADD9_DEGREES), "add9"),
      colorQ(key, "V", "sus4"),
      resolveQ(key, "V", "sus4"),
      colorCompQ(key),
    ];
  }
  if (spec.kind === "walk") {
    const key = pick(KEYS);
    return [...walkUpSequence(key), ...walkDownSequence(key)];
  }
  if (spec.kind === "space") {
    // warm the shells up, then the leave-room run: comp three bars, lay out
    // for the fill. Rest bars only mean something against a groove, so without
    // audio this falls back to plain shell comping (the notes still transfer).
    const key = pick(KEYS);
    const loop = spec.prog || unit.prog || [];
    const qs = sample([...new Set(loop)], 2).map((deg) => voicingQ(key, deg));
    qs.push(opts.timed ? leaveRoomQ(key, loop) : compProgQ(key, loop));
    if (opts.timed) qs.push(leaveRoomQ(pick(KEYS), loop));
    return qs;
  }

  return [];
}

/* ---------------- lesson builder ----------------
   A lesson is shaped as an arc, not a shuffled pile: warm up on recall (one
   direction per concept — never both for the same chord in a lesson), apply it
   in context (build / complete-the-progression), then end on something musical
   (a progression, an ear loop, a song). The order is deliberate; randomness is
   in *which* concepts/keys/loops/phrasings appear so re-runs still vary. The
   "put it together" synthesis lives in its own nodes (see buildCapstone /
   buildTrackCapstone), not inside the per-lesson arc.

   `opts.midi`: when a MIDI keyboard is connected, lessons bias toward *playing*
   — recall becomes play-the-chord (build), and a play-the-whole-loop capstone
   is appended. Without MIDI the lesson is unchanged (no glass-piano busywork). */
export function buildLesson(unit, idx, opts = {}) {
  // Per-key units describe their lessons with a `plan`; each lesson is built by
  // kind (scale / chords / prog) rather than the warm-up→core→payoff arc below.
  if (unit.plan) return buildPlanLesson(unit, unit.plan[idx], opts);

  const modes = unit.modes || ["translate"];
  const midi = Boolean(opts.midi);
  // One recall question per concept, alternating direction so the same fact is
  // never asked both ways in a lesson. With a keyboard — or once the concept
  // is production-strength — recall is *playing* the chord (build). `keyAt`
  // lets seventh units roam keys.
  const recall = (degs, keyAt) =>
    shuffle(degs).map((deg, i) => {
      const key = keyAt(deg);
      if (midi || isStrong(opts.mastery, key, deg)) return buildQ(key, deg);
      return i % 2 === 0 ? num2chordQ(key, deg) : chord2numQ(key, deg);
    });

  // A seventh unit drills its degrees across *every* key (not a single home
  // key), so the learner sees Imaj7/V7/… everywhere rather than only in C.
  if (unit.seventh) {
    const per = Math.ceil(unit.degrees.length / unit.lessons);
    const focus = unit.degrees.slice(idx * per, idx * per + per);
    const warmup = recall(focus, () => pick(KEYS));
    const core = [];
    if (!midi && modes.includes("build")) core.push(buildQ(pick(KEYS), pick(focus)));
    // a ii7–V7–Imaj7 turnaround to apply the sevenths in a real cadence
    const turnaround = ["ii7", "V7", "Imaj7"];
    core.push(completeQ(pick(KEYS), turnaround));
    if (opts.timed) core.push(timedProgQ(pick(KEYS), turnaround));
    else if (midi) core.push(playProgQ(pick(KEYS), turnaround));
    return [...warmup, ...core];
  }

  // A voicing unit teaches *which notes to play*: one shell/rootless voicing
  // per focus degree. Seventh-voicing units roam every key (like the seventh
  // unit); triad-voicing units stay in the unit's home key. No ear/song payoff —
  // the lesson is purely about getting the guide tones under the hands.
  if (unit.voicing) {
    const per = Math.ceil(unit.degrees.length / unit.lessons);
    const focus = unit.degrees.slice(idx * per, idx * per + per);
    // An inversions unit drills voice leading: each focus chord in 1st & 2nd
    // inversion (which note is in the bass), graded octave-aware.
    if (unit.inversions) {
      return shuffle(
        focus.flatMap((deg) => [
          inversionQ(unit.key, deg, 1),
          inversionQ(unit.key, deg, 2),
        ])
      );
    }
    const keyAt = () => (unit.seventhVoicing ? pick(KEYS) : unit.key);
    return shuffle(focus).map((deg) => voicingQ(keyAt(), deg));
  }

  // A mixed (review) unit has no single home key — it's all application: a
  // complete-the-loop opener, then ear / song payoffs across unlocked keys.
  if (unit.mixed) {
    const keyPool = unit.minor ? MINOR_KEYS : KEYS;
    const progPool = unit.minor ? MINOR_PROGS : COMMON_PROGS;
    const qs = [completeQ(pick(keyPool), pick(progPool))];
    if (modes.includes("ear")) {
      const ek = pick(keyPool);
      const el = pick(progPool);
      const md = earMoveDegrees(ek, el);
      if (md.length) qs.push(earMoveQ(ek, pick(md)));
      qs.push(earQ(ek, el));
      qs.push(earQ(pick(keyPool), pick(progPool)));
    }
    if (modes.includes("song")) {
      qs.push(songQ(pick(songPoolFor(unit)), "numbers"));
      qs.push(songQ(pick(songPoolFor(unit)), "title"));
    }
    if (opts.timed) qs.push(timedProgQ(pick(keyPool), pick(progPool)));
    else if (midi) qs.push(playProgQ(pick(keyPool), pick(progPool)));
    return qs;
  }

  // A standard unit: warm up → apply in context → end on something musical.
  const per = Math.ceil(unit.degrees.length / unit.lessons);
  const focus = unit.degrees.slice(idx * per, idx * per + per);
  const progPool = unit.minor ? MINOR_PROGS : COMMON_PROGS;
  const loop = unit.prog || pick(progPool);

  const warmup = recall(focus, () => unit.key);

  const core = [];
  if (!midi && modes.includes("build")) core.push(buildQ(unit.key, pick(focus)));
  core.push(completeQ(unit.key, loop));

  const payoff = [];
  if (unit.prog) payoff.push(progressionQ(unit.key, unit.prog));
  if (modes.includes("ear")) {
    // the single-change atom first, then the whole loop by ear
    const md = earMoveDegrees(unit.key, loop);
    if (md.length) payoff.push(earMoveQ(unit.key, pick(md)));
    payoff.push(earQ(unit.key, loop));
  }
  if (modes.includes("song")) {
    const s = unit.song ? songByTitle(unit.song) : pick(songPoolFor(unit));
    if (s) payoff.push(songQ(s, "numbers"));
  }
  // End the lesson by playing the whole loop — in time when audio can drive a
  // groove, free-time under bare MIDI.
  if (opts.timed) payoff.push(timedProgQ(unit.key, loop));
  else if (midi) payoff.push(playProgQ(unit.key, loop));

  return [...warmup, ...core, ...payoff];
}

/* ---------------- single-question builders (for practice games) ----------------
   The practice games run their own loops; these emit one fresh question of the
   right type from the learner's unlocked material. */
export const randomTranslateQ = (pairs) => {
  const p = pick(pairs);
  return Math.random() < 0.5 ? num2chordQ(p.key, p.deg) : chord2numQ(p.key, p.deg);
};

export const randomBuildTarget = (pairs) => {
  const p = pick(pairs);
  return buildQ(p.key, p.deg);
};

export const randomEarQ = (key) => earQ(key, pick(progPoolFor(key)));

export const randomPredictQ = (key) => predictQ(key, pick(progPoolFor(key)));

export const randomSongQ = (songs) =>
  songQ(pick(songs), Math.random() < 0.5 ? "numbers" : "title");

export const randomTonicQ = (key) => tonicQ(key);

export const randomCadenceQ = (key) => cadenceQ(key);

/* ---------------- review lesson (spaced repetition) ----------------
   Build a short lesson weighted toward the learner's weakest concepts. The
   focus pool is the weakest ~half of unlocked concepts (at least 3 when
   available); each focus concept contributes a translate question and,
   half the time, a build question. Membership of the focus pool is
   deterministic for a given mastery snapshot — only the question variety and
   presentation order are randomised — so weak concepts measurably resurface
   more often than mastered ones. */
export function buildReviewLesson(pairs = [], mastery = {}, size = 8, opts = {}) {
  if (!pairs.length) return [];
  const midi = Boolean(opts.midi);
  const focusN = Math.min(pairs.length, Math.max(3, Math.ceil(pairs.length / 2)));
  // With `opts.today`, staleness counts: a decayed box reviews as weak.
  const focus = weakestConcepts(pairs, mastery, focusN, opts.today || null);
  const qs = [];
  focus.forEach(({ key, deg }, i) => {
    if (midi || isStrong(mastery, key, deg)) {
      qs.push(buildQ(key, deg)); // strong concepts review as play-the-chord
    } else {
      // alternate direction so a concept is drilled both ways across reviews
      qs.push(i % 2 === 0 ? num2chordQ(key, deg) : chord2numQ(key, deg));
      if (Math.random() < 0.5) qs.push(buildQ(key, deg));
    }
  });
  const lesson = shuffle(qs).slice(0, Math.min(qs.length, size));
  // The tracked dimensions review too: the weakest drilled ear concepts
  // (`key:deg:ear`) resurface as single-change questions, and the weakest
  // shells (`key:deg:shell`) as build-the-voicing questions.
  const weakDim = (dim, n = 2) =>
    Object.keys(mastery)
      .filter((id) => id.endsWith(`:${dim}`))
      .map((id) => ({
        ...splitConcept(id),
        s: conceptStrength(mastery, id, opts.today || null),
      }))
      .filter((x) => x.s < MASTERY_MAX && entryFor(x.key, x.deg))
      .sort((a, b) => a.s - b.s)
      .slice(0, n);
  weakDim("ear").forEach(({ key, deg }) => lesson.push(earMoveQ(key, deg)));
  weakDim("shell").forEach(({ key, deg }) => lesson.push(voicingQ(key, deg)));
  // Close the review by playing a loop in a reviewed key — in time when audio
  // can drive a groove, free-time under bare MIDI.
  if (opts.timed) lesson.push(timedProgQ(focus[0].key, pick(COMMON_PROGS)));
  else if (midi) lesson.push(playProgQ(focus[0].key, pick(COMMON_PROGS)));
  return lesson;
}

/* ---------------- targeted drills (the practice space) ----------------
   An open practice room for nailing a single key cold: its scale, then the
   chords built from that scale. `scaleQ` is a build question whose target is
   the seven scale notes (play the root and it still has to be the whole scale).
   `buildKeyDrill` opens with the scale, then drills that key's diatonic chords
   in mixed directions. Not unlock-gated — it's a place to grind. */

// The diatonic degrees a key drill grinds (the everyday triads; the diminished
// vii°/ii° are left out of the drill set to keep it musical).
const DRILL_DEGREES_MAJOR = ["I", "ii", "iii", "IV", "V", "vi"];
const DRILL_DEGREES_MINOR = ["i", "III", "iv", "v", "VI", "VII"];

// "Build the scale" — a build question targeting all seven scale notes.
export const scaleQ = (key) => {
  const minor = MINOR_KEYS.includes(key);
  return {
    type: "build",
    key,
    scale: true,
    label: `the ${key} ${minor ? "minor" : "major"} scale`,
    targetNotes: scaleFor(key) || [],
  };
};

// A focused drill for one key: meet the scale, then grind its chords. Mixes
// number→chord, chord→number, and build so the key gets worked from every angle.
export function buildKeyDrill(key) {
  const minor = MINOR_KEYS.includes(key);
  const degs = minor ? DRILL_DEGREES_MINOR : DRILL_DEGREES_MAJOR;
  const chords = shuffle(degs).map((deg) => {
    const r = Math.random();
    if (r < 0.4) return num2chordQ(key, deg);
    if (r < 0.75) return chord2numQ(key, deg);
    return buildQ(key, deg);
  });
  return [scaleQ(key), ...chords];
}

/* ---------------- keyboard flow (MIDI-only rapid drill) ----------------
   One "call" at a time for the flow drill: either a chord (by degree) or a
   single scale note (by number). `notes` is the pitch-class set the player has
   to hold to clear it. Pure; the screen rolls a fresh one after each hit. */
const rollFlow = (key, mode) => {
  if (mode === "notes") {
    const scale = scaleFor(key) || [];
    const i = Math.floor(Math.random() * scale.length);
    return { mode: "notes", key, label: String(i + 1), name: scale[i], notes: [scale[i]] };
  }
  const minor = MINOR_KEYS.includes(key);
  const degs = minor ? DRILL_DEGREES_MINOR : DRILL_DEGREES_MAJOR;
  const deg = degs[Math.floor(Math.random() * degs.length)];
  const e = entryFor(key, deg);
  return {
    mode: "chords",
    key,
    label: deg,
    name: chordFor(key, deg),
    notes: e ? e.notes.slice(0, 3) : [],
  };
};

// The next flow call, avoiding an immediate repeat of `prevLabel`.
export const flowTarget = (key, mode = "chords", prevLabel = null) => {
  for (let n = 0; n < 6; n += 1) {
    const t = rollFlow(key, mode);
    if (t.label !== prevLabel) return t;
  }
  return rollFlow(key, mode);
};

/* ---------------- daily challenge + daily goal ----------------
   A once-a-day mixed lesson: the question selection is driven by a tiny
   seeded PRNG keyed off the day, so the same user gets the same challenge all
   day (deterministic, not per-run-random). It draws from everything the
   learner has *unlocked* (issue #89) — the daily is the natural mixed-review
   surface, but it never tests material the path hasn't introduced. Veterans
   with everything unlocked get the full curriculum, and a brand-new learner
   gets a short scale-only daily so the streak works from day one.

   The daily XP goal is a soft target for *all* XP earned today (lessons +
   games), read off the per-day history log. */

// A small deterministic PRNG so a date seeds a stable challenge. mulberry32 over
// a cheap string hash — not cryptographic, just stable and well-distributed.
const hashStr = (s) => {
  let h = 1779033703 ^ String(s).length;
  for (let i = 0; i < String(s).length; i += 1) {
    h = Math.imul(h ^ String(s).charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
};
const mulberry32 = (seed) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export const DAILY_GOAL_XP = 30;

// Build the deterministic daily challenge for a `YYYY-M-D` day key: a fixed mix
// of translate / build / progression / ear / song questions whose (key, degree,
// progression, song) choices are seeded by the date. Two calls with the same
// date and unlock state pick the same material; option ordering inside a
// question is still cosmetic (left to the generators).
//
// `opts.unlocked` (an array of unlocked `key:deg` concepts) filters the
// material pools; `opts.songs` likewise for the song question. Omit both (or
// pass null, as unlockAll does) for the whole curriculum — that's exactly the
// veteran behavior.
export function buildDailyChallenge(dateKey, opts = {}) {
  const midi = Boolean(opts.midi);
  const rnd = mulberry32(hashStr(`daily:${dateKey}`));
  const spick = (arr) => arr[Math.floor(rnd() * arr.length)];

  // ---- material pools, filtered to what the path has introduced (#89) ----
  const unlocked = Array.isArray(opts.unlocked) ? opts.unlocked : null;
  const playable = DEGREES.filter((d) => d !== "vii°"); // playable triads
  const keyDegs = (k) =>
    unlocked ? playable.filter((d) => unlocked.includes(`${k}:${d}`)) : playable;
  const keys = KEYS.filter((k) => keyDegs(k).length > 0);
  // a key can only be quizzed on loops whose every chord it has unlocked
  const loopsFor = (k) =>
    COMMON_PROGS.filter((p) => p.every((d) => keyDegs(k).includes(d)));
  const loopKeys = keys.filter((k) => loopsFor(k).length > 0);
  // the daily quizzes major keys, so its song pool stays major too
  const songs = opts.songs ? opts.songs : MAJOR_SONGS;

  // Day one (no chords unlocked yet): a short scale-only daily in the first
  // key, so the streak mechanic works from the very first lesson.
  if (!keys.length) {
    return Array.from({ length: 5 }, () =>
      scaleNoteQ("C", spick(["num2note", "note2num"]), Math.floor(rnd() * 7))
    );
  }

  const k1 = spick(keys);
  const k2 = spick(keys);
  const loopPick = () => {
    const k = spick(loopKeys);
    return { k, loop: spick(loopsFor(k)) };
  };

  // With a keyboard, the daily leans on playing: play-the-chord recall and a
  // play-the-whole-loop capstone, keeping the date-seeded selection.
  const qs = [];
  if (midi) {
    qs.push(buildQ(k1, spick(keyDegs(k1))));
    qs.push(buildQ(k2, spick(keyDegs(k2))));
  } else {
    qs.push(num2chordQ(k1, spick(keyDegs(k1))));
    qs.push(chord2numQ(k1, spick(keyDegs(k1))));
    qs.push(buildQ(k2, spick(keyDegs(k2))));
    qs.push(num2chordQ(k2, spick(keyDegs(k2))));
  }
  if (loopKeys.length) {
    const a = loopPick();
    qs.push(midi ? completeQ(a.k, a.loop) : progressionQ(a.k, a.loop));
    const b = loopPick();
    qs.push(earQ(b.k, b.loop));
  }
  if (songs.length) {
    qs.push(songQ(spick(songs), rnd() < 0.5 ? "numbers" : "title"));
  }
  // Close on real playing when audio can drive the groove (the glass piano /
  // typing keyboard are enough to comp a loop); free-time under bare MIDI.
  if (loopKeys.length && (opts.timed || midi)) {
    const c = loopPick();
    qs.push((opts.timed ? timedProgQ : playProgQ)(c.k, c.loop));
  }
  return qs;
}

// Has today's daily challenge already been completed?
export const dailyDoneToday = (dailyDate, today) =>
  Boolean(today) && dailyDate === today;

// Progress toward the daily XP goal from the per-day history log.
export const dailyGoalProgress = (history = {}, today, goal = DAILY_GOAL_XP) => {
  const earned = (history && history[today] && history[today].xp) || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((earned / goal) * 100)) : 0;
  return { earned, goal, pct, met: earned >= goal };
};

/* ---------------- unlock state (for practice games) ----------------
   Which (key, degree) concepts a learner has unlocked, so a concept counts once
   the lesson that introduces it is complete. For a per-key unit that's each
   completed `chords` lesson's degrees; for the older shape it's the unit's
   degrees split across its lessons (the same split buildLesson uses). Scale and
   progression lessons add no new concept; mixed/seventh/voicing units add none. */
const unitUnlockPairs = (u, completed) => {
  const out = [];
  if (u.plan) {
    if (u.seventh) return out; // sevenths stay out of the triad practice games
    u.plan.forEach((l, i) => {
      if (l.kind === "chords" && completed[`${u.id}-${i}`]) {
        (l.degrees || []).forEach((deg) => out.push(`${u.key}:${deg}`));
      }
    });
    return out;
  }
  if (u.mixed || u.seventh || u.voicing) return out;
  const per = Math.ceil(u.degrees.length / u.lessons);
  for (let i = 0; i < u.lessons; i += 1) {
    if (completed[`${u.id}-${i}`]) {
      u.degrees.slice(i * per, i * per + per).forEach((deg) => out.push(`${u.key}:${deg}`));
    }
  }
  return out;
};

export function unlockedConcepts(completed = {}) {
  const out = [];
  UNITS.forEach((u) => out.push(...unitUnlockPairs(u, completed)));
  return [...new Set(out)];
}

// The (key, degree) concepts one lesson node teaches — the chords lessons of
// plan units. The home path uses this to mark a *completed* node "due" when
// its concepts' Leitner boxes have gone stale (see conceptsDue), so review
// pressure shows up on the path itself instead of hiding behind a menu item.
export const lessonConceptPairs = (unit, idx) => {
  const spec = unit && unit.plan && unit.plan[idx];
  if (!spec || spec.kind !== "chords") return [];
  return (spec.degrees || []).map((deg) => ({ key: unit.key, deg }));
};

// Every (key, deg) concept taught by lessons *before* idx in this unit — the
// "earlier in this unit" list for the lesson runtime's reference panel (see
// LessonScreen's learned-so-far panel). Only chords lessons introduce
// concepts, so scale/prog/voicing/etc. lessons contribute nothing, same as
// lessonConceptPairs. Dedupes in case a plan ever repeats a degree.
export const conceptsThroughLesson = (unit, idx) => {
  if (!unit || !unit.plan || typeof idx !== "number") return [];
  const seen = new Set();
  const out = [];
  for (let i = 0; i < idx; i += 1) {
    lessonConceptPairs(unit, i).forEach((pair) => {
      const id = `${pair.key}:${pair.deg}`;
      if (seen.has(id)) return;
      seen.add(id);
      out.push(pair);
    });
  }
  return out;
};

// The (key, deg) concepts touched by an arbitrary set of graded questions —
// for review / daily / drill lessons, which have no unit + idx to look up
// curriculum concepts through (see NumberSense's startReviewLesson etc.).
// Reads both a question's single q.deg (build/translate/inversion/voicelead)
// and a whole progression's q.degrees (playprog/ear/complete/...); rest bars
// and duplicates are dropped.
export const questionConcepts = (questions = []) => {
  const seen = new Set();
  const out = [];
  const add = (key, deg) => {
    if (!key || !deg || deg === REST) return;
    const id = `${key}:${deg}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ key, deg });
  };
  (questions || []).forEach((q) => {
    if (!q || !q.key) return;
    if (q.deg) add(q.key, q.deg);
    if (Array.isArray(q.degrees)) q.degrees.forEach((d) => add(q.key, d));
  });
  return out;
};

// The progressions practiced in a lesson's playprog questions — dedupes by
// key + degree sequence for the reference panel's optional "progressions
// practiced this lesson" list. Only playprog questions carry a whole loop.
export const lessonProgressions = (questions = []) => {
  const seen = new Set();
  const out = [];
  (questions || []).forEach((q) => {
    if (!q || q.type !== "playprog" || !q.key || !Array.isArray(q.degrees)) return;
    const id = `${q.key}:${q.degrees.join(",")}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ key: q.key, degrees: q.degrees, labels: q.labels || null });
  });
  return out;
};

// Where a lesson's timed groove finale starts, or -1 if this run has none.
// The done screen uses it to offer "back to the groove": a run that died at
// (or is being replayed for) the finale can restart from this index instead
// of redoing the whole drill.
export const grooveIndex = (questions = []) =>
  (questions || []).findIndex((q) => q && q.type === "playprog" && q.timed);

// Every (key:deg) concept the curriculum teaches — granted wholesale when the
// settings unlock code is entered, so a returning player isn't re-gated behind
// the path after a curriculum change.
export const allConcepts = () => {
  const out = [];
  UNITS.forEach((u) => {
    if (!u.plan || u.seventh) return; // sevenths stay out of the triad games
    u.plan.forEach((l) => {
      if (l.kind === "chords") (l.degrees || []).forEach((d) => out.push(`${u.key}:${d}`));
    });
  });
  return [...new Set(out)];
};

// A decorative "unlock everything" code for the settings screen. Like the
// other soft gates in this app it ships in the bundle — not real security,
// just a friendly bypass so existing players aren't blocked by new gating.
export const UNLOCK_CODE = "KeyoniIsCool";

const keyOf = (pair) => pair.split(":")[0];

// Distinct keys the learner has touched.
export const unlockedKeys = (unlocked) => [...new Set(unlocked.map(keyOf))];

// Keys with at least three unlocked degrees — enough to play a real progression.
export const earReadyKeys = (unlocked) =>
  unlockedKeys(unlocked).filter(
    (k) => unlocked.filter((p) => keyOf(p) === k).length >= 3
  );

// Starter songs whose key the learner has reached (forgiving — by key, not by
// every individual degree), so the name-that-progression game has material.
export const unlockedSongs = (unlocked) => {
  const keys = new Set(unlockedKeys(unlocked));
  return STARTER_SONGS.filter((s) => keys.has(s.key));
};

// Is a given practice game playable with the current unlock state?
export function gameAvailable(kind, unlocked) {
  if (kind === "translate") return unlocked.length >= 3;
  if (kind === "builder") return unlocked.length >= 3;
  if (kind === "ear") return earReadyKeys(unlocked).length >= 1;
  if (kind === "song") return unlockedSongs(unlocked).length >= 1;
  // The ear games (find-the-tonic, cadence, next-chord prediction) need a key
  // with enough material to play a convincing move — same bar as ear training.
  if (kind === "tonic" || kind === "cadence" || kind === "predict")
    return earReadyKeys(unlocked).length >= 1;
  return false;
}

// Walk the curriculum in order and find the first lesson after which `kind`
// becomes playable — for the "unlocks · unit N · lesson M" badge.
export function gameUnlockPoint(kind) {
  const completed = {};
  for (let ui = 0; ui < UNITS.length; ui += 1) {
    const u = UNITS[ui];
    for (let i = 0; i < lessonCount(u); i += 1) {
      completed[`${u.id}-${i}`] = true;
      if (gameAvailable(kind, unlockedConcepts(completed))) {
        return { unitNum: ui + 1, lessonNum: i + 1 };
      }
    }
  }
  return null;
}

/* ---------------- chart mode (#87) ----------------
   The band-simulation payoff: someone says "it's in G — 1, 5, 6m, 4" and
   you're in. A chart is a numbers-only form — ordered sections, each a loop of
   degrees × repeats, every section in a key — flattened to a per-bar
   performance the transport (startChart) and the grading (timedBarResult, per
   bar in the bar's own key) both read. Mid-chart key changes are announced a
   bar ahead ("→ now in D") — the on-the-fly transposition drill the number
   system exists for. All pure; ChartScreen owns the audio clock. */

// XP for a passed chart run — a hair above a clean lesson, it's the exam.
export const CHART_XP = 25;

// Flatten a chart's sections into performance bars:
//   [{ deg, key, section, sec, call? }]
// `call` marks the bar during which the NEXT bar's key change is announced.
export const chartBars = (sections = []) => {
  const bars = [];
  sections.forEach((s, sec) => {
    for (let r = 0; r < (s.repeats || 1); r += 1)
      (s.loop || []).forEach((deg) =>
        bars.push({ deg, key: s.key, section: s.name, sec })
      );
  });
  bars.forEach((b, i) => {
    const next = bars[i + 1];
    if (next && next.key !== b.key) b.call = next.key;
  });
  return bars;
};

// The keys whose unlocked degrees cover at least one full common loop — the
// pool chart mode may throw at the player. Null/absent `unlocked` = everything
// (the unlock-all switch).
const chartLoopsIn = (key, unlocked) =>
  COMMON_PROGS.filter((p) =>
    p.every((d) => !unlocked || unlocked.includes(`${key}:${d}`))
  );
export const chartKeys = (unlocked = null) =>
  KEYS.filter((k) => chartLoopsIn(k, unlocked).length > 0);

// Chart mode is worth opening once a key change is possible — two chart-ready
// keys. (Roughly: after the second key's progression work, as intended.)
export const chartReady = (unlocked = null) => chartKeys(unlocked).length >= 2;

// Build a random chart from the unlocked material: verse ×2 + chorus, then the
// chorus again in a NEW key (announced a bar ahead) when a second key is
// available. The player never picks the key — that's the drill.
export function buildChart(opts = {}) {
  const unlocked = Array.isArray(opts.unlocked) ? opts.unlocked : null;
  const keys = chartKeys(unlocked);
  if (!keys.length) return null;
  const key = pick(keys);
  // the key change: another key that shares a playable loop with the first
  let key2 = key;
  let pool = chartLoopsIn(key, unlocked);
  if (opts.keyChange !== false) {
    const others = shuffle(keys.filter((k) => k !== key));
    for (const k of others) {
      const shared = pool.filter((p) => chartLoopsIn(k, unlocked).includes(p));
      if (shared.length) {
        key2 = k;
        pool = shared;
        break;
      }
    }
  }
  const verse = pick(pool);
  const chorusPool = pool.filter((p) => p !== verse);
  const chorus = chorusPool.length ? pick(chorusPool) : verse;
  const sections = [
    { name: "verse", key, loop: verse, repeats: 2 },
    { name: "chorus", key, loop: chorus, repeats: 1 },
    { name: "chorus", key: key2, loop: chorus, repeats: 1 },
  ];
  return { title: null, key, keys: key2 === key ? [key] : [key, key2], sections, bars: chartBars(sections) };
}

// A starter song's form as a playable chart (all sections in the song's key).
export function songChart(song) {
  if (!song || !song.form) return null;
  const sections = song.form.map((s) => ({ ...s, key: song.key }));
  return {
    title: song.title,
    artist: song.artist,
    key: song.key,
    keys: [song.key],
    sections,
    bars: chartBars(sections),
  };
}

// The songs chart mode can run as forms — unlocked ones that carry a `form`.
export const chartSongs = (unlocked = null) =>
  (unlocked ? unlockedSongs(unlocked) : STARTER_SONGS).filter((s) => s.form);
